import React from "react";
import {
  RotateCcw,
  Grid,
  Airplay,
  Move,
  CheckCircle,
  XCircle,
  Sun,
  Wind,
  Cloud,
  Clipboard,
  Zap,
  Box,
  HelpCircle,
} from "react-feather";
import { primaryColors } from "../../../common/Colors";
import { Loader } from "../../../Components/Loader";
import { Select } from "../Form/Select";
import { errorCodes } from "../consts/consts";
import {
  domainType,
  gridSizes,
  windGridSizes,
  staticExpertGridSizes,
  simulationModeOptions,
  amountDirectionsOptions,
  topographyInterpolationModeOptions,
  topographyInterpolationModeOutflowOptions,
  gridWidthOptions,
} from "./RasterizationSettingsConst";
import { NumericInput } from "../Form/NumericInput";
import * as actions from "../../../redux/actions/rasterAreaActions";
import { putCaseGeo } from "../../../api/caseApi";
import { reproject } from "reproject";
import { wgDef } from "../../../common/utmDef";
import gp from "geojson-precision";
import { selectCaseSimulationNotifications } from "../../../redux/selectors/simulationSelector";
import { getCaseSetup, postCaseSetup } from "../../../api/simulationApi";
import {
  requestRasterizationPreviewAsync,
  requestStaticDriverAsync,
} from "../../../api/rasterizationApi";
import { setCaseDrawnGeojsonUrl } from "../../../redux/actions/caseActions";
import { uploadBlobToAzure, downloadBlobAsync } from "../../../api/storageApi";
import {
  setDrawnPolygonGeojsonCrs,
  createSingleTreeGeoJson,
  createSingleEmissionDrawnGeoJson,
  createSingleMapPoiDrawnGeoJson,
  createSingleWindTurbineDrawnGeoJson,
} from "../../../redux/actions/mapActions";
import { cloneDeep } from "lodash";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { withRouter } from "react-router";
import Icon from "../../Common/Icon/index";
import "./RasterizationSettings.scss";

const helpUrl =
  "https://palm_gui.pages.fraunhofer.de/palmgui_handbuch/domainmap.html#sec-domainrasterization";

/** Plain { path, name } (or string / array) for raster API payloads. */
function toRasterHeightFilePayload(fileValue) {
  if (fileValue == null) {
    return fileValue;
  }
  if (Array.isArray(fileValue)) {
    return fileValue.map(toRasterHeightFilePayload);
  }
  if (typeof fileValue === "string") {
    return fileValue;
  }
  if (typeof fileValue === "object") {
    const path = fileValue.path;
    const name = fileValue.name;
    if (path != null || name != null) {
      const o = {};
      if (path != null) o.path = path;
      if (name != null) o.name = name;
      return o;
    }
  }
  return fileValue;
}

class BasicRasterizationSettings extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isRasterProcessing: false,
      rasterError: null,
      isStaticDriverProcessing: false,
    };
    this.createRasterPreview = this.createRasterPreview.bind(this);
    this.createStaticDriver = this.createStaticDriver.bind(this);
    this.loadPrecursorCaseOptions = this.loadPrecursorCaseOptions.bind(this);
    this.saveWindTurbineSettings = this.saveWindTurbineSettings.bind(this);
  }

  async loadPrecursorCaseOptions() {
    const { caseDetailsDto, currentCase } = this.props;
    if (!caseDetailsDto || !Array.isArray(caseDetailsDto)) {
      return [];
    }

    const precursorCases = [];

    try {
      const caseChecks = await Promise.all(
        caseDetailsDto.map(async (caseItem) => {
          // Exclude the current case from the list
          if (currentCase && caseItem.id === currentCase.id) {
            return null;
          }

          try {
            const caseSetup = await getCaseSetup(caseItem.id);
            if (
              typeof caseSetup !== "string" &&
              caseSetup.setup?.settings?.wind_turbine_parameters?.mode ===
              "Precursor"
            ) {
              return {
                label: caseItem.name,
                value: caseItem.id,
              };
            }
            return null;
          } catch (error) {
            return null;
          }
        })
      );

      caseChecks.forEach((caseOption) => {
        if (caseOption) {
          precursorCases.push(caseOption);
        }
      });

      console.log("Found precursor cases:", precursorCases);
      return precursorCases;
    } catch (error) {
      return [];
    }
  }

  async saveWindTurbineSettings() {
    if (this.props.currentCase?.applicationField !== 5) {
      return;
    }

    const simulationMode = this.props.rasterArea.simulationMode;
    let windTurbineSettings = {};
    console.log(simulationMode);

    if (simulationMode === "Precursor") {
      windTurbineSettings = {
        caseId: this.props.currentCase.id,
        settings: {
          initialization_parameters: {
            nz_setting:
              this.props.windTurbineSettings?.nzSetting?.value || "Manual",
            nz: this.props.windTurbineSettings?.nz || 1000,
            dz_stretch_level:
              this.props.windTurbineSettings?.stretchLevel || 500,
            dz_stretch_level_factor:
              this.props.windTurbineSettings?.StretchFactor || 1.08,
            dz_max: this.props.windTurbineSettings?.dzMax || 1000,
            height_auto: this.props.windTurbineSettings?.heightAuto || 1000,
            nx: this.props.rasterArea?.rasterGridXNumber - 1 || 0,
            ny: this.props.rasterArea?.rasterGridYNumber - 1 || 0,
            resolution: this.props.rasterArea?.rasterGridWidth || 0,
          },
          runtime_parameters: {
            npex: this.props.windTurbineSettings?.coresInXDirection || 10,
            npey: this.props.windTurbineSettings?.coresInYDirection || 10,
            total_cores:
              (this.props.windTurbineSettings?.coresInXDirection || 10) *
              (this.props.windTurbineSettings?.coresInYDirection || 10),
            end_time:
              this.props.windTurbineSettings?.simulationDuration || 10800,
            netcdf_data_format:
              this.props.windTurbineSettings?.netcdfDataFormat?.value || "3",
            skip_time_data_output:
              this.props.windTurbineSettings?.skipTimeBeforeDataOutput || 0,
            dt_run_control:
              this.props.windTurbineSettings?.timeIntervalRunControlOutput ||
              60,
            dt_data_output:
              this.props.windTurbineSettings?.timeIntervalDataOutput || 0,
            dt_data_output_av:
              this.props.windTurbineSettings?.timeIntervalAverageDataOutput ||
              0,
            dt_dots:
              this.props.windTurbineSettings?.timeIntervalTimeseriesOutput || 0,
            dt_dopr:
              this.props.windTurbineSettings?.timeIntervalProfilesOutput || 0,
            dt_do3d: this.props.windTurbineSettings?.timeInterval3DOutput || 0,
            section_yz:
              this.props.windTurbineSettings?.section_yz?.value || "Empty",
            section_xz:
              this.props.windTurbineSettings?.section_xz?.value || "Empty",
            mask_k_over_surface:
              this.props.windTurbineSettings?.mask_k_over_surface || 20,
            averaging_interval:
              this.props.windTurbineSettings?.averagingInterval || 0,
            dt_averaging_input:
              this.props.windTurbineSettings?.timePeriodForAveraging || 0,
            averaging_interval_pr:
              this.props.windTurbineSettings?.averagingIntervalForProfiles || 0,
            dt_averaging_input_pr:
              this.props.windTurbineSettings?.timePeriodForProfilesAveraging ||
              0,
          },
          wind_turbine_parameters: {
            mode: "Precursor",
            precursor_case: null,
            wind_profile_settings_mode:
              this.props.windTurbineSettings?.windProfileSettingsMode?.value ||
              "Manual",
            ug:
              this.props.windTurbineSettings?.uComponentOfGeostrophicWind || 9,
            vg:
              this.props.windTurbineSettings?.vComponentOfGeostrophicWind || 9,
            hub_height: this.props.windTurbineSettings?.hubHeight || 100,
            wind_speed_hh:
              this.props.windTurbineSettings?.windSpeedAtHubHeight || 0,
            wind_dir_hh:
              this.props.windTurbineSettings?.windDirectionAtHubHeight || 0,
            profile_temperature:
              this.props.windTurbineSettings?.temperatureProfile?.value ||
              "Neutral",
            profile_subsidance:
              this.props.windTurbineSettings?.substanceProfile?.value ||
              "Profile1",
            rotor_table:
              this.props.windTurbineSettings?.rotorTable?.value || "Preset1",
            rotor_speed: this.props.windTurbineSettings?.rotorSpeed || 0.1,
            yaw_angle: this.props.windTurbineSettings?.yawAngle || 0,
            tilt_angle:
              this.props.windTurbineSettings?.rotorPlaneTiltAngle || 0,
            generator_power_rated:
              this.props.windTurbineSettings?.generatorRatedMechanicalPower ||
              5296610.0,
            speed_control:
              this.props.windTurbineSettings?.activateSpeedControl || false,
            pitch_control:
              this.props.windTurbineSettings?.activatePitchControl || false,
            yaw_control:
              this.props.windTurbineSettings?.activateYawControl || false,
            yaw_speed:
              this.props.windTurbineSettings?.yawActuatorSpeed || 0.005236,
            tower_cd:
              this.props.windTurbineSettings?.dragCoefficientTower || 1.2,
            segment_length_tangential:
              this.props.windTurbineSettings?.tangentialLengthOfRotorSegments ||
              1,
            segment_width_radial:
              this.props.windTurbineSettings?.radialWidthOfRotorSegments || 0.5,
            n_airfoils: this.props.windTurbineSettings?.numberOfAirfoils || 8,
            roughness_length:
              this.props.windTurbineSettings?.roughnessLengthDomain || 0.1,
            surface_heatflux:
              this.props.windTurbineSettings?.surfaceHeatflux || 1,
            rayleigh_damping_height:
              this.props.windTurbineSettings?.rayleighDampingHeight || 500,
            rayleigh_dampening_factor:
              this.props.windTurbineSettings?.rayleighDampeningFactor || 0.5,
            inertial_oscillation_damping_factor:
              this.props.windTurbineSettings
                ?.intertialOscillationDampeningFactor || 0.9,
            canopy_drag_coeff:
              this.props.windTurbineSettings?.canopyDragCoefficient || 0.15,
            wall_heatflux_top_surface:
              this.props.windTurbineSettings?.wallHeatfluxTopSurface || 1,
          },
        },
      };
    } else if (simulationMode === "FullRun") {
      const precursorCaseId = this.props.rasterArea.precursorCase?.value;
      console.log(precursorCaseId);
      if (precursorCaseId) {
        try {
          const precursorCaseSetup = await getCaseSetup(precursorCaseId);
          if (
            typeof precursorCaseSetup !== "string" &&
            precursorCaseSetup.setup?.settings
          ) {
            windTurbineSettings = {
              caseId: this.props.currentCase.id,
              settings: {
                ...precursorCaseSetup.setup.settings,
                wind_turbine_parameters: {
                  ...precursorCaseSetup.setup.settings.wind_turbine_parameters,
                  mode: "FullRun",
                  precursor_case: precursorCaseId,
                  amount_directions: this.props.rasterArea.amountDirections,
                  single_inflow_wind_direction:
                    this.props.rasterArea.amountDirections === "1"
                      ? this.props.rasterArea.singleInflowWindDirection ?? 270.0
                      : null,
                },
              },
            };
          }
        } catch (error) {
          console.warn(
            "Failed to load precursor case setup, using default values:",
            error
          );
        }
      }
    }

    await postCaseSetup(windTurbineSettings);
    console.log("Wind turbine settings saved successfully");
  }

  async componentDidMount() {
    if (this.props.rasterArea.rasterPreview !== null) {
      this.props.onSingleRasterLoadVisibility();
    } else if (this.props.rasterArea.parentRasterPreview !== null) {
      this.props.onNestedRasterLoadVisibility();
    }

    if (this.props.currentCase?.applicationField === 5) {
      const precursorCases = await this.loadPrecursorCaseOptions();
      this.props.setPrecursorCaseOptions(precursorCases);

      // Only fallback after we've actually loaded precursor options.
      // Do not overwrite a persisted FullRun just because options are still loading.
      if (
        this.props.rasterArea.simulationMode === "FullRun" &&
        (!precursorCases || precursorCases.length === 0)
      ) {
        this.props.setSimulationMode("Precursor");
      }
    }

    if (
      (this.props.currentCase &&
        !this.props.rasterArea.rasterPreview &&
        this.props.currentCase?.rasterPreviewJob?.status === 2) ||
      this.props.currentCase?.rasterPreviewJob?.status === 2 ||
      this.props.currentCase?.rasterPreviewJob?.status === "Completed"
    ) {
      if (!this.props.currentCase.rasterPreviewJob.childResultUrl) {
        const rasterPreviewGeoJson = await downloadBlobAsync(
          this.props.currentCase.rasterPreviewJob.resultUrl
        );
        this.props.getRasterPreview({
          rasterPreview: rasterPreviewGeoJson.data,
          crsDef: this.props.crsDef,
        });
      } else {
        const childRasterPreviewGeoJson = await downloadBlobAsync(
          this.props.currentCase.rasterPreviewJob.childResultUrl
        );
        const parentRasterPreviewGeoJson = await downloadBlobAsync(
          this.props.currentCase.rasterPreviewJob.resultUrl
        );
        this.props.getNestedRasterPreview({
          parentRasterPreview: parentRasterPreviewGeoJson.data,
          childRasterPreview: childRasterPreviewGeoJson.data,
          crsDef: this.props.crsDef,
        });
      }
    }

  }

  async componentDidUpdate(prevProps) {
    // Reset simulation mode to "Precursor" if "FullRun" is selected but no precursor cases are available
    if (
      this.props.currentCase?.applicationField === 5 &&
      this.props.rasterArea.simulationMode === "FullRun" &&
      (!this.props.rasterArea.precursorCaseOptions ||
        this.props.rasterArea.precursorCaseOptions.length === 0)
    ) {
      this.props.setSimulationMode("Precursor");
    }

    if (
      prevProps.currentCase?.rasterPreviewJob &&
      this.props.currentCase?.rasterPreviewJob
    ) {
      if (
        prevProps.currentCase.rasterPreviewJob.status !== 2 &&
        this.props.currentCase.rasterPreviewJob.status === 2
      ) {
        this.props.setRasterProcessing(false);

        if (!this.props.currentCase.rasterPreviewJob.childResultUrl) {
          const rasterPreviewGeoJson = await downloadBlobAsync(
            this.props.currentCase.rasterPreviewJob.resultUrl
          );

          this.props.getRasterPreview({
            rasterPreview: rasterPreviewGeoJson.data,
            crsDef: this.props.crsDef,
          });
        } else {
          const childRasterPreviewGeoJson = await downloadBlobAsync(
            this.props.currentCase.rasterPreviewJob.childResultUrl
          );
          const parentRasterPreviewGeoJson = await downloadBlobAsync(
            this.props.currentCase.rasterPreviewJob.resultUrl
          );
          this.props.setRasterProcessing(false);

          this.props.getNestedRasterPreview({
            parentRasterPreview: parentRasterPreviewGeoJson.data,
            childRasterPreview: childRasterPreviewGeoJson.data,
            crsDef: this.props.crsDef,
          });
        }
      } else if (
        prevProps.currentCase.rasterPreviewJob.status !== 3 &&
        this.props.currentCase.rasterPreviewJob.status === 3
      ) {
        this.props.setRasterProcessing(false);
      }
    }

    if (
      prevProps.currentCase?.staticDriverJob &&
      this.props.currentCase?.staticDriverJob
    ) {
      if (
        prevProps.currentCase.staticDriverJob.status !== 2 &&
        this.props.currentCase.staticDriverJob.status === 2
      ) {
        this.setState({
          isStaticDriverProcessing: false,
        });
      } else if (
        prevProps.currentCase.staticDriverJob.status !== 3 &&
        this.props.currentCase.staticDriverJob.status === 3
      ) {
        this.setState({
          isStaticDriverProcessing: false,
        });
      }
    }
  }

  async createRasterPreview() {
    this.setState({
      staticDriverProcessing: true,
    });

    this.props.setRasterProcessing(true);

    const caseEntity = this.props.loadedProject.caseDetailsDto.find(
      (c) => c.id === this.props.match.params.caseId
    );

    const geojsonUrl = caseEntity.verifiedGeojsonUrl;
    const drawnGeojsonUrl = `${this.props.loadedProject.id}/${caseEntity.id}/drawnGeojsonUrl.json`;
    const polygonDataUrl = `${this.props.loadedProject.id}/${caseEntity.id}/polygonData.json`;

    await this.props.createSingleTreeGeoJson(this.props.loadedProject.epsg);
    const geoJsonWg = cloneDeep(this.props.geoJsonWg);

    const emissionGeoJsonWg = cloneDeep(this.props.emissionGeoJsonWg);
    const mapPoiGeoJsonWg = cloneDeep(this.props.mapPoiGeoJsonWg);
    const windTurbineGeoJsonWg = cloneDeep(this.props.windTurbineGeoJsonWg);

    if (emissionGeoJsonWg.features.length > 0) {
      geoJsonWg.features.push(...emissionGeoJsonWg.features);
    }
    if (mapPoiGeoJsonWg.features.length > 0) {
      geoJsonWg.features.push(...mapPoiGeoJsonWg.features);
    }
    if (windTurbineGeoJsonWg.features.length > 0) {
      geoJsonWg.features.push(...windTurbineGeoJsonWg.features);
    }

    const geoJson = reproject(geoJsonWg, wgDef, this.props.crsDef);
    const geoJsonUTM = gp.parse(geoJson, 2);

    const drawnPolygonGeojsonWg = this.props.drawnPolygonGeojsonWg;
    drawnPolygonGeojsonWg.crs.properties.name = `urn:ogc:def:crs:EPSG::${this.props.loadedProject.epsg}`;

    const drawnPolygonGeoJsonConv = reproject(
      drawnPolygonGeojsonWg,
      wgDef,
      this.props.crsDef
    );

    const drawnPolygonGeoJsonUtm = gp.parse(drawnPolygonGeoJsonConv, 2);

    await this.props.createSingleEmissionDrawnGeoJson(
      this.props.loadedProject.epsg
    );
    await this.props.createSingleMapPoiDrawnGeoJson(
      this.props.loadedProject.epsg
    );
    await this.props.createSingleWindTurbineDrawnGeoJson(
      this.props.loadedProject.epsg
    );

    if (this.props.emissionDrawnGeoJsonUtm.features.length !== 0) {
      drawnPolygonGeoJsonUtm.features.push(
        ...this.props.emissionDrawnGeoJsonUtm.features
      );
    }

    if (this.props.mapPoiDrawnGeoJsonUtm.features.length !== 0) {
      drawnPolygonGeoJsonUtm.features.push(
        ...this.props.mapPoiDrawnGeoJsonUtm.features
      );
    }
    if (this.props.windTurbineDrawnGeoJsonUtm.features.length !== 0) {
      drawnPolygonGeoJsonUtm.features.push(
        ...this.props.windTurbineDrawnGeoJsonUtm.features
      );
    }

    this.props.treeGeoJsonUtm.features.map((x) => {
      return drawnPolygonGeoJsonUtm.features.push(x);
    });

    if (!!geojsonUrl) {
      await uploadBlobToAzure(geojsonUrl, JSON.stringify(geoJsonUTM));
    }

    await uploadBlobToAzure(
      polygonDataUrl,
      JSON.stringify(this.props.polygonData)
    );

    await uploadBlobToAzure(
      drawnGeojsonUrl,
      JSON.stringify(drawnPolygonGeoJsonUtm)
    );

    const request = {
      geojsonUrl: geojsonUrl,
      polygonDataUrl: polygonDataUrl,
      drawnGeojsonUrl: drawnGeojsonUrl,
    };

    await putCaseGeo(caseEntity.id, request);
    this.props.setCaseDrawnGeojsonUrl({
      drawnGeojsonUrl: drawnGeojsonUrl,
      polygonDataUrl: polygonDataUrl,
    });

    const rasterMethodIndex =
      this.props.rasterArea.rasterMethod === "TouchAll" ? 2 : 1;
    const soilType = this.props.rasterArea.soilType;
    const soilTypeIndex =
      soilType === "Coarse"
        ? 1
        : soilType === "Medium"
          ? 2
          : soilType === "MediumFine"
            ? 3
            : soilType === "Fine"
              ? 4
              : soilType === "VeryFine"
                ? 5
                : soilType === "Organic"
                  ? 6
                  : soilType === "UserDefined"
                    ? 99
                    : "";

    const externalFiles = cloneDeep(this.props.files);

    const tiffFiles = externalFiles.filter((x) => {
      return x.type === "image/tiff";
    });

    const cityGmlFiles = externalFiles.filter((x) => {
      return x.type === "application/gml+xml" || x.name.endsWith('.gml');
    });

    const bhParentFile =
      this.props.rasterArea.buildingHeights === "fromGeotiff" &&
        this.props.rasterArea.buildingHeightsFile === null
        ? tiffFiles[0]
        : this.props.rasterArea.buildingHeights === "fromGeotiff"
          ? tiffFiles.find((x) => {
            return (
              x.name === this.props.rasterArea.buildingHeightsFile ||
              x.path === this.props.rasterArea.buildingHeightsFile ||
              x.name === this.props.rasterArea.buildingHeightsFile?.name ||
              x.path === this.props.rasterArea.buildingHeightsFile?.path
            );
          })
          : this.props.rasterArea.buildingHeights === "fromCityGML"
            ? cityGmlFiles.filter((x) =>
              this.props.rasterArea.buildingHeightsCityGmlFiles.includes(x.path)
            )
            : this.props.rasterArea.buildingHeightsFile;

    const thParentFile =
      this.props.rasterArea.treeHeights === "fromGeotiff" &&
        this.props.rasterArea.treeHeightsFile === null
        ? tiffFiles[0]
        : this.props.rasterArea.treeHeights === "fromGeotiff"
          ? tiffFiles.find((x) => {
            return (
              x.name === this.props.rasterArea.treeHeightsFile ||
              x.path === this.props.rasterArea.treeHeightsFile ||
              x.name === this.props.rasterArea.treeHeightsFile?.name ||
              x.path === this.props.rasterArea.treeHeightsFile?.path
            );
          })
          : this.props.rasterArea.treeHeightsFile;

    const roughnessGeotiffFile =
      this.props.rasterArea.useRoughnessGeotiff &&
        this.props.rasterArea.roughnessGeotiffFile === null
        ? tiffFiles[0]
        : this.props.rasterArea.useRoughnessGeotiff
          ? tiffFiles.find((x) => {
            return (
              x.name === this.props.rasterArea.roughnessGeotiffFile ||
              x.path === this.props.rasterArea.roughnessGeotiffFile ||
              x.name === this.props.rasterArea.roughnessGeotiffFile?.name ||
              x.path === this.props.rasterArea.roughnessGeotiffFile?.path
            );
          })
          : this.props.rasterArea.roughnessGeotiffFile;

    if (this.props.rasterArea.domainType === "SingleDomain") {
      const rasterGridWidth =
        this.props.currentCase.applicationField === 2
          ? 1
          : this.props.rasterArea.rasterGridWidth;
      const resolution = rasterGridWidth;

      const xLength = resolution * this.props.rasterArea.rasterGridXNumber;
      const yLength = resolution * this.props.rasterArea.rasterGridYNumber;
      const x_min = this.props.rasterArea.rasterCenterLngUtm - xLength / 2;
      const x_max =
        Number(this.props.rasterArea.rasterCenterLngUtm) + Number(xLength) / 2;
      const y_min = this.props.rasterArea.rasterCenterLatUtm - yLength / 2;
      const y_max =
        Number(this.props.rasterArea.rasterCenterLatUtm) + Number(yLength) / 2;

      const request = {
        rasterParameters: {
          x_min: Number(x_min),
          x_max: x_max,
          y_min: Number(y_min),
          y_max: y_max,
          season: this.props.rasterArea.season,
          soil_Type: soilTypeIndex,
          resolution: resolution,
          rasterMethod: rasterMethodIndex,
          rasterizationPriority: this.props.rasterArea.rasterizationPriority,
          fillValue: this.props.rasterArea.fillValue.rasterValue,
          surfaceBelowTree:
            this.props.rasterArea.fillValueBelowTreePatches.rasterValue,
          additionalData: {
            globalParameters: {
              soilParameterization: "GloballyUniform",
              waterTemperatureParameteriation:
                this.props.rasterArea.globalWaterTemperatureSource,
              waterTemperatureGlobal:
                this.props.rasterArea.globalWaterTemperature,
              verticalResolutionMode:
                this.props.rasterArea.verticalResolutionMode,
              verticalResolutionMeter:
                this.props.rasterArea.verticalResolutionMode === "Manual"
                  ? this.props.rasterArea.verticalResolutionMeter
                  : this.props.rasterArea.rasterGridWidth,
              parentOffsetX: this.props.rasterArea.parentOffsetX,
              parentOffsetY: this.props.rasterArea.parentOffsetY,
              normalizeTopography: this.props.rasterArea.normalizeTopography,
              buildingSettings: {
                bhParent: this.props.rasterArea.buildingHeights,
                bhParentFile: toRasterHeightFilePayload(bhParentFile),
                bhChild: null,
                bhChildFile: null,
              },
              treeSettings: {
                thParent: this.props.rasterArea.treeHeights,
                thParentFile: toRasterHeightFilePayload(thParentFile),
                thChild: null,
                thChildFile: null,
              },
              roughnessSettings: {
                useRoughnessGeotiff: this.props.rasterArea.useRoughnessGeotiff,
                roughnessGeotiffFile: toRasterHeightFilePayload(
                  roughnessGeotiffFile
                ),
              },
              domainBufferSettings: {
                gridPointsNorth: this.props.rasterArea.gridPointsNorth,
                gridPointsSouth: this.props.rasterArea.gridPointsSouth,
                gridPointsWest: this.props.rasterArea.gridPointsWest,
                gridPointsEast: this.props.rasterArea.gridPointsEast,
                bufferFillType: this.props.rasterArea.bufferFillType,
                bufferFillValue:
                  this.props.rasterArea.bufferFillValue.rasterValue,
                bufferMethod: this.props.rasterArea.bufferMethod,
                bufferTopographyInterpMethod:
                  this.props.rasterArea.bufferTopographyInterpMethod,
              },
            },
          },
        },
        geojsonUrl: geojsonUrl,
        polygonDataUrl: polygonDataUrl,
        drawnGeojsonUrl: drawnGeojsonUrl,
      };

      if (soilType === "UserDefined") {
        request.rasterParameters.additionalData.globalParameters.soilParameters =
        {
          alpha_vg: this.props.rasterArea.soilAlphaCoefficient,
          l_vg: this.props.rasterArea.lSoilCoefficient,
          n_vg: this.props.rasterArea.nSoilCoefficient,
          gamma_w_sat: this.props.rasterArea.soilHydraulicConductivity,
          m_sat: this.props.rasterArea.soilMoistureSaturation,
          m_fc: this.props.rasterArea.soilMoistureCapacity,
          m_wilt: this.props.rasterArea.soilMoistureWilting,
          m_res: this.props.rasterArea.soilMoistureContent,
        };
      }

      if (this.props.currentCase.applicationField === 5) {
        request.rasterParameters.additionalData.windTurbineParameters = {
          mode: this.props.rasterArea.simulationMode,
          precursor_case: this.props.rasterArea.precursorCase?.value
            ? {
              value: this.props.rasterArea.precursorCase.value,
              label: this.props.rasterArea.precursorCase.label,
            }
            : null,
          amount_directions: this.props.rasterArea.amountDirections,
          single_inflow_wind_direction:
            this.props.rasterArea.amountDirections === "1"
              ? this.props.rasterArea.singleInflowWindDirection ?? 270.0
              : null,
          // domain_rotation_clockwise:
          //   this.props.rasterArea.domainRotationClockwise || 0,
          grid_points_inflow: this.props.rasterArea.additionalGridpointsInflow,
          interpolation_distance_inflow:
            this.props.rasterArea.gridpointsForInflowTopographyInterpolation,
          interpolation_mode_inflow:
            this.props.rasterArea.topographyInterpolationModeInflow,
          grid_points_southnorth:
            this.props.rasterArea.additionalGridpointsSouthNorthEach,
          interpolation_distance_outflow:
            this.props.rasterArea.gridpointsForOutflowTopographyInterpolation,
          interpolation_mode_southnorth:
            this.props.rasterArea.topographyInterpolationModeSouthNorth,
          grid_points_outflow:
            this.props.rasterArea.additionalGridpointsOutflow,
          interpolation_mode_outflow:
            this.props.rasterArea.topographyInterpolationModeOutflow,
          interpolation_start_after_last_turbine:
            this.props.rasterArea
              .gridpointsAfterWhichOutflowInterpolationStarts,
          add_hub_height_onto_topography:
            this.props.rasterArea.addHubHeightOntoTopography,
        };
      }
      console.log(JSON.stringify(request, null, 2));
      await this.saveWindTurbineSettings();

      await requestRasterizationPreviewAsync(
        this.props.match.params.caseId,
        request
      );
      this.props.setSingleRasterCaseProps(request.rasterParameters);
    } else if (this.props.rasterArea.domainType === "NestedDomain") {
      const parentResolution = this.props.rasterArea.parentGridSize;
      const childResolution = this.props.rasterArea.childGridSize;
      const xParentLength =
        parentResolution * this.props.rasterArea.parentRasterGridXNumber;
      const yParentLength =
        parentResolution * this.props.rasterArea.parentRasterGridYNumber;
      const x_parent_min =
        this.props.rasterArea.rasterCenterLngUtm - xParentLength / 2;
      const x_parent_max =
        Number(this.props.rasterArea.rasterCenterLngUtm) +
        Number(xParentLength) / 2;
      const y_parent_min =
        this.props.rasterArea.rasterCenterLatUtm - yParentLength / 2;
      const y_parent_max =
        Number(this.props.rasterArea.rasterCenterLatUtm) +
        Number(yParentLength) / 2;

      const xChildLength =
        childResolution * this.props.rasterArea.childRasterGridXNumber;
      const yChildLength =
        childResolution * this.props.rasterArea.childRasterGridYNumber;
      const x_child_min =
        this.props.rasterArea.rasterCenterLngUtm - xChildLength / 2;
      const x_child_max =
        Number(this.props.rasterArea.rasterCenterLngUtm) +
        Number(xChildLength) / 2;
      const y_child_min =
        this.props.rasterArea.rasterCenterLatUtm - yChildLength / 2;
      const y_child_max =
        Number(this.props.rasterArea.rasterCenterLatUtm) +
        Number(yChildLength) / 2;

      const bhChildFile =
        this.props.rasterArea.childBuildingHeights === "fromGeotiff" &&
          this.props.rasterArea.childBuildingHeightsFile === null
          ? tiffFiles[0]
          : this.props.rasterArea.childBuildingHeights === "fromGeotiff"
            ? tiffFiles.find((x) => {
              return (
                x.name === this.props.rasterArea.childBuildingHeightsFile ||
                x.name ===
                this.props.rasterArea.childBuildingHeightsFile.name ||
                x.path === this.props.rasterArea.childBuildingHeightsFile ||
                x.path === this.props.rasterArea.childBuildingHeightsFile?.path
              );
            })
            : this.props.rasterArea.childBuildingHeightsFile;

      const thChildFile =
        this.props.rasterArea.childTreeHeights === "fromGeotiff" &&
          this.props.rasterArea.childTreeHeightsFile === null
          ? tiffFiles[0]
          : this.props.rasterArea.childTreeHeights === "fromGeotiff"
            ? tiffFiles.find((x) => {
              return (
                x.name === this.props.rasterArea.childTreeHeightsFile ||
                x.name === this.props.rasterArea.childTreeHeightsFile.name ||
                x.path === this.props.rasterArea.childTreeHeightsFile ||
                x.path === this.props.rasterArea.childTreeHeightsFile?.path
              );
            })
            : this.props.rasterArea.childTreeHeightsFile;

      const request = {
        rasterParameters: {
          x_min: x_parent_min + Number(this.props.rasterArea.parentOffsetX),
          x_max: x_parent_max + Number(this.props.rasterArea.parentOffsetX),
          y_min: y_parent_min + Number(this.props.rasterArea.parentOffsetY),
          y_max: y_parent_max + Number(this.props.rasterArea.parentOffsetY),
          season: this.props.rasterArea.season,
          soil_Type: soilTypeIndex,
          resolution: parentResolution,
          rasterMethod: rasterMethodIndex,
          rasterizationPriority: this.props.rasterArea.rasterizationPriority,
          fillValue: this.props.rasterArea.fillValue.rasterValue,
          surfaceBelowTree:
            this.props.rasterArea.fillValueBelowTreePatches.rasterValue,
          additionalData: {
            globalParameters: {
              soilParameterization: "GloballyUniform",
              waterTemperatureParameteriation:
                this.props.rasterArea.globalWaterTemperatureSource,
              waterTemperatureGlobal:
                this.props.rasterArea.globalWaterTemperature,
              verticalResolutionMode:
                this.props.rasterArea.verticalResolutionMode,
              verticalResolutionMeter:
                this.props.rasterArea.verticalResolutionMode === "Manual"
                  ? this.props.rasterArea.verticalResolutionMeter
                  : this.props.rasterArea.rasterGridWidth,
              parentOffsetX: this.props.rasterArea.parentOffsetX,
              parentOffsetY: this.props.rasterArea.parentOffsetY,
              normalizeTopography: this.props.rasterArea.normalizeTopography,
              buildingSettings: {
                bhParent: this.props.rasterArea.buildingHeights,
                bhParentFile: toRasterHeightFilePayload(bhParentFile),
                bhChild: this.props.rasterArea.childBuildingHeights,
                bhChildFile: toRasterHeightFilePayload(bhChildFile),
              },
              treeSettings: {
                thParent: this.props.rasterArea.treeHeights,
                thParentFile: toRasterHeightFilePayload(thParentFile),
                thChild: this.props.rasterArea.childTreeHeights,
                thChildFile: toRasterHeightFilePayload(thChildFile),
              },
              roughnessSettings: {
                useRoughnessGeotiff: this.props.rasterArea.useRoughnessGeotiff,
                roughnessGeotiffFile: toRasterHeightFilePayload(
                  roughnessGeotiffFile
                ),
              },
              domainBufferSettings: {
                gridPointsNorth: this.props.rasterArea.gridPointsNorth,
                gridPointsSouth: this.props.rasterArea.gridPointsSouth,
                gridPointsWest: this.props.rasterArea.gridPointsWest,
                gridPointsEast: this.props.rasterArea.gridPointsEast,
                bufferFillType: this.props.rasterArea.bufferFillType,
                bufferFillValue:
                  this.props.rasterArea.bufferFillValue.rasterValue,
                bufferMethod: this.props.rasterArea.bufferMethod,
                bufferTopographyInterpMethod:
                  this.props.rasterArea.bufferTopographyInterpMethod,
              },
            },
          },
        },
        childRasterParameters: {
          x_min: x_child_min,
          x_max: x_child_max,
          y_min: y_child_min,
          y_max: y_child_max,
          season: this.props.rasterArea.season,
          soil_Type: soilTypeIndex,
          resolution: childResolution,
          rasterMethod: rasterMethodIndex,
          rasterizationPriority: this.props.rasterArea.rasterizationPriority,
          fillValue: this.props.rasterArea.fillValue.rasterValue,
          surfaceBelowTree:
            this.props.rasterArea.fillValueBelowTreePatches.rasterValue,
        },
        geojsonUrl: geojsonUrl,
        polygonDataUrl: polygonDataUrl,
        drawnGeojsonUrl: drawnGeojsonUrl,
      };

      if (soilType === "UserDefined") {
        request.rasterParameters.additionalData.globalParameters.soilParameters =
        {
          alpha_vg: this.props.rasterArea.soilAlphaCoefficient,
          l_vg: this.props.rasterArea.lSoilCoefficient,
          n_vg: this.props.rasterArea.nSoilCoefficient,
          gamma_w_sat: this.props.rasterArea.soilHydraulicConductivity,
          m_sat: this.props.rasterArea.soilMoistureSaturation,
          m_fc: this.props.rasterArea.soilMoistureCapacity,
          m_wilt: this.props.rasterArea.soilMoistureWilting,
          m_res: this.props.rasterArea.soilMoistureContent,
        };
      }

      if (this.props.currentCase.applicationField === 5) {
        request.rasterParameters.additionalData.windTurbineParameters = {
          mode: this.props.rasterArea.simulationMode,
          precursor_case: this.props.rasterArea.precursorCase?.value
            ? {
              value: this.props.rasterArea.precursorCase.value,
              label: this.props.rasterArea.precursorCase.label,
            }
            : null,
          amount_directions: this.props.rasterArea.amountDirections,
          single_inflow_wind_direction:
            this.props.rasterArea.amountDirections === "1"
              ? this.props.rasterArea.singleInflowWindDirection ?? 270.0
              : null,
          // domain_rotation_clockwise:
          //   this.props.rasterArea.domainRotationClockwise || 0,
          grid_points_inflow: this.props.rasterArea.additionalGridpointsInflow,
          interpolation_distance_inflow:
            this.props.rasterArea.gridpointsForInflowTopographyInterpolation,
          interpolation_mode_inflow:
            this.props.rasterArea.topographyInterpolationModeInflow,
          grid_points_southnorth:
            this.props.rasterArea.additionalGridpointsSouthNorthEach,
          interpolation_distance_outflow:
            this.props.rasterArea.gridpointsForOutflowTopographyInterpolation,
          interpolation_mode_southnorth:
            this.props.rasterArea.topographyInterpolationModeSouthNorth,
          grid_points_outflow:
            this.props.rasterArea.additionalGridpointsOutflow,
          interpolation_mode_outflow:
            this.props.rasterArea.topographyInterpolationModeOutflow,
          interpolation_start_after_last_turbine:
            this.props.rasterArea
              .gridpointsAfterWhichOutflowInterpolationStarts,
          add_hub_height_onto_topography:
            this.props.rasterArea.addHubHeightOntoTopography,
        };
        await this.saveWindTurbineSettings();
      }

      console.log(JSON.stringify(request, null, 2));

      await requestRasterizationPreviewAsync(
        this.props.match.params.caseId,
        request
      );
      this.props.setNestedRasterCaseProps(request);
    }

    this.props.setDrawnPolygonGeojsonCrs(this.props.loadedProject.epsg);
  }

  async createStaticDriver() {
    this.setState({ isStaticDriverProcessing: true });
    const caseEntity = this.props.loadedProject.caseDetailsDto.find(
      (c) => c.id === this.props.match.params.caseId
    );

    const geojsonUrl = caseEntity.verifiedGeojsonUrl;
    const drawnGeojsonUrl = `${this.props.loadedProject.id}/${caseEntity.id}/drawnGeojsonUrl.json`;
    const polygonDataUrl = `${this.props.loadedProject.id}/${caseEntity.id}/polygonData.json`;

    await this.props.createSingleTreeGeoJson(this.props.loadedProject.epsg);
    const drawnPolygonGeoJsonConv = reproject(
      this.props.drawnPolygonGeojsonWg,
      wgDef,
      this.props.crsDef
    );

    const externalFiles = cloneDeep(this.props.files);

    const tiffFiles = externalFiles.filter((x) => {
      return x.type === "image/tiff";
    });

    const cityGmlFiles = externalFiles.filter((x) => {
      return x.type === "application/gml+xml" || x.name.endsWith('.gml');
    });

    const drawnPolygonGeoJsonUtm = gp.parse(drawnPolygonGeoJsonConv, 2);
    this.props.treeGeoJsonUtm.features.map((x) => {
      return drawnPolygonGeoJsonUtm.features.push(x);
    });

    const rasterMethodIndex =
      this.props.rasterArea.rasterMethod === "TouchAll" ? 2 : 1;
    const soilType = this.props.rasterArea.soilType;
    const soilTypeIndex =
      soilType === "Coarse"
        ? 1
        : soilType === "Medium"
          ? 2
          : soilType === "MediumFine"
            ? 3
            : soilType === "Fine"
              ? 4
              : soilType === "VeryFine"
                ? 5
                : soilType === "Organic"
                  ? 6
                  : soilType === "UserDefined"
                    ? 99
                    : "";

    const bhParentFile =
      this.props.rasterArea.buildingHeights === "fromGeotiff" &&
        this.props.rasterArea.buildingHeightsFile === null
        ? tiffFiles[0]
        : this.props.rasterArea.buildingHeights === "fromGeotiff"
          ? tiffFiles.find((x) => {
            return (
              x.name === this.props.rasterArea.buildingHeightsFile ||
              x.path === this.props.rasterArea.buildingHeightsFile ||
              x.name === this.props.rasterArea.buildingHeightsFile?.name ||
              x.path === this.props.rasterArea.buildingHeightsFile?.path
            );
          })
          : this.props.rasterArea.buildingHeights === "fromCityGML"
            ? cityGmlFiles.filter((x) =>
              this.props.rasterArea.buildingHeightsCityGmlFiles.includes(x.path)
            )
            : this.props.rasterArea.buildingHeightsFile;

    const thParentFile =
      this.props.rasterArea.treeHeights === "fromGeotiff" &&
        this.props.rasterArea.treeHeightsFile === null
        ? tiffFiles[0]
        : this.props.rasterArea.treeHeights === "fromGeotiff"
          ? tiffFiles.find((x) => {
            return (
              x.name === this.props.rasterArea.treeHeightsFile ||
              x.path === this.props.rasterArea.treeHeightsFile ||
              x.name === this.props.rasterArea.treeHeightsFile?.name ||
              x.path === this.props.rasterArea.treeHeightsFile?.path
            );
          })
          : this.props.rasterArea.treeHeightsFile;

    const roughnessGeotiffFile =
      this.props.rasterArea.useRoughnessGeotiff &&
        this.props.rasterArea.roughnessGeotiffFile === null
        ? tiffFiles[0]
        : this.props.rasterArea.useRoughnessGeotiff
          ? tiffFiles.find((x) => {
            return (
              x.name === this.props.rasterArea.roughnessGeotiffFile ||
              x.path === this.props.rasterArea.roughnessGeotiffFile ||
              x.name === this.props.rasterArea.roughnessGeotiffFile?.name ||
              x.path === this.props.rasterArea.roughnessGeotiffFile?.path
            );
          })
          : this.props.rasterArea.roughnessGeotiffFile;

    if (this.props.rasterArea.domainType === "SingleDomain") {
      const rasterGridWidth =
        this.props.currentCase.applicationField === 2
          ? 1
          : this.props.rasterArea.rasterGridWidth;
      const resolution = rasterGridWidth;
      const xLength = resolution * this.props.rasterArea.rasterGridXNumber;
      const yLength = resolution * this.props.rasterArea.rasterGridYNumber;
      const x_min = this.props.rasterArea.rasterCenterLngUtm - xLength / 2;
      const x_max =
        Number(this.props.rasterArea.rasterCenterLngUtm) + Number(xLength) / 2;
      const y_min = this.props.rasterArea.rasterCenterLatUtm - yLength / 2;
      const y_max =
        Number(this.props.rasterArea.rasterCenterLatUtm) + Number(yLength) / 2;

      const request = {
        rasterParameters: {
          x_min: Number(x_min),
          x_max: x_max,
          y_min: Number(y_min),
          y_max: y_max,
          season: this.props.rasterArea.season,
          soil_Type: soilTypeIndex,
          resolution: resolution,
          rasterMethod: rasterMethodIndex,
          rasterizationPriority: this.props.rasterArea.rasterizationPriority,
          fillValue: this.props.rasterArea.fillValue.rasterValue,
          surfaceBelowTree:
            this.props.rasterArea.fillValueBelowTreePatches.rasterValue,
          additionalData: {
            globalParameters: {
              soilParameterization: "GloballyUniform",
              waterTemperatureParameteriation:
                this.props.rasterArea.globalWaterTemperatureSource,
              waterTemperatureGlobal:
                this.props.rasterArea.globalWaterTemperature,
              verticalResolutionMode:
                this.props.rasterArea.verticalResolutionMode,
              verticalResolutionMeter:
                this.props.rasterArea.verticalResolutionMode === "Manual"
                  ? this.props.rasterArea.verticalResolutionMeter
                  : rasterGridWidth,
              parentOffsetX: this.props.rasterArea.parentOffsetX,
              parentOffsetY: this.props.rasterArea.parentOffsetY,
              normalizeTopography: this.props.rasterArea.normalizeTopography,
              buildingSettings: {
                bhParent: this.props.rasterArea.buildingHeights,
                bhParentFile: toRasterHeightFilePayload(bhParentFile),
                bhChild: null,
                bhChildFile: null,
              },
              treeSettings: {
                thParent: this.props.rasterArea.treeHeights,
                thParentFile: toRasterHeightFilePayload(thParentFile),
                thChild: null,
                thChildFile: null,
              },
              roughnessSettings: {
                useRoughnessGeotiff: this.props.rasterArea.useRoughnessGeotiff,
                roughnessGeotiffFile: toRasterHeightFilePayload(
                  roughnessGeotiffFile
                ),
              },
              domainBufferSettings: {
                gridPointsNorth: this.props.rasterArea.gridPointsNorth,
                gridPointsSouth: this.props.rasterArea.gridPointsSouth,
                gridPointsWest: this.props.rasterArea.gridPointsWest,
                gridPointsEast: this.props.rasterArea.gridPointsEast,
                bufferFillType: this.props.rasterArea.bufferFillType,
                bufferFillValue:
                  this.props.rasterArea.bufferFillValue.rasterValue,
                bufferMethod: this.props.rasterArea.bufferMethod,
                bufferTopographyInterpMethod:
                  this.props.rasterArea.bufferTopographyInterpMethod,
              },
            },
          },
        },
        geojsonUrl: geojsonUrl,
        polygonDataUrl: polygonDataUrl,
        drawnGeojsonUrl: drawnGeojsonUrl,
      };

      if (soilType === "UserDefined") {
        request.rasterParameters.additionalData.globalParameters.soilParameters =
        {
          alpha_vg: this.props.rasterArea.soilAlphaCoefficient,
          l_vg: this.props.rasterArea.lSoilCoefficient,
          n_vg: this.props.rasterArea.nSoilCoefficient,
          gamma_w_sat: this.props.rasterArea.soilHydraulicConductivity,
          m_sat: this.props.rasterArea.soilMoistureSaturation,
          m_fc: this.props.rasterArea.soilMoistureCapacity,
          m_wilt: this.props.rasterArea.soilMoistureWilting,
          m_res: this.props.rasterArea.soilMoistureContent,
        };
      }

      if (this.props.currentCase.applicationField === 5) {
        request.rasterParameters.additionalData.windTurbineParameters = {
          mode: this.props.rasterArea.simulationMode,
          precursor_case: this.props.rasterArea.precursorCase?.value
            ? {
              value: this.props.rasterArea.precursorCase.value,
              label: this.props.rasterArea.precursorCase.label,
            }
            : null,
          amount_directions: this.props.rasterArea.amountDirections,
          single_inflow_wind_direction:
            this.props.rasterArea.amountDirections === "1"
              ? this.props.rasterArea.singleInflowWindDirection ?? 270.0
              : null,
          // domain_rotation_clockwise:
          //   this.props.rasterArea.domainRotationClockwise || 0,
          grid_points_inflow: this.props.rasterArea.additionalGridpointsInflow,
          interpolation_distance_inflow:
            this.props.rasterArea.gridpointsForInflowTopographyInterpolation,
          interpolation_mode_inflow:
            this.props.rasterArea.topographyInterpolationModeInflow,
          grid_points_southnorth:
            this.props.rasterArea.additionalGridpointsSouthNorthEach,
          interpolation_distance_outflow:
            this.props.rasterArea.gridpointsForOutflowTopographyInterpolation,
          interpolation_mode_southnorth:
            this.props.rasterArea.topographyInterpolationModeSouthNorth,
          grid_points_outflow:
            this.props.rasterArea.additionalGridpointsOutflow,
          interpolation_mode_outflow:
            this.props.rasterArea.topographyInterpolationModeOutflow,
          interpolation_start_after_last_turbine:
            this.props.rasterArea
              .gridpointsAfterWhichOutflowInterpolationStarts,
          add_hub_height_onto_topography:
            this.props.rasterArea.addHubHeightOntoTopography,
        };
      }

      console.log(JSON.stringify(request, null, 2));
      await this.saveWindTurbineSettings();
      await requestStaticDriverAsync(this.props.match.params.caseId, request);
    } else if (this.props.rasterArea.domainType === "NestedDomain") {
      const parentResolution = this.props.rasterArea.parentGridSize;
      const childResolution = this.props.rasterArea.childGridSize;
      const xParentLength =
        parentResolution * this.props.rasterArea.parentRasterGridXNumber;
      const yParentLength =
        parentResolution * this.props.rasterArea.parentRasterGridYNumber;
      const x_parent_min =
        this.props.rasterArea.rasterCenterLngUtm - xParentLength / 2;
      const x_parent_max =
        Number(this.props.rasterArea.rasterCenterLngUtm) +
        Number(xParentLength) / 2;
      const y_parent_min =
        this.props.rasterArea.rasterCenterLatUtm - yParentLength / 2;
      const y_parent_max =
        Number(this.props.rasterArea.rasterCenterLatUtm) +
        Number(yParentLength) / 2;

      const xChildLength =
        childResolution * this.props.rasterArea.childRasterGridXNumber;
      const yChildLength =
        childResolution * this.props.rasterArea.childRasterGridYNumber;
      const x_child_min =
        this.props.rasterArea.rasterCenterLngUtm - xChildLength / 2;
      const x_child_max =
        Number(this.props.rasterArea.rasterCenterLngUtm) +
        Number(xChildLength) / 2;
      const y_child_min =
        this.props.rasterArea.rasterCenterLatUtm - yChildLength / 2;
      const y_child_max =
        Number(this.props.rasterArea.rasterCenterLatUtm) +
        Number(yChildLength) / 2;

      const bhChildFile =
        this.props.rasterArea.childBuildingHeights === "fromGeotiff" &&
          this.props.rasterArea.childBuildingHeightsFile === null
          ? tiffFiles[0]
          : this.props.rasterArea.childBuildingHeights === "fromGeotiff"
            ? tiffFiles.find((x) => {
              return (
                x.name === this.props.rasterArea.childBuildingHeightsFile ||
                x.name ===
                this.props.rasterArea.childBuildingHeightsFile.name ||
                x.path === this.props.rasterArea.childBuildingHeightsFile ||
                x.path === this.props.rasterArea.childBuildingHeightsFile?.path
              );
            })
            : this.props.rasterArea.childBuildingHeightsFile;

      const thChildFile =
        this.props.rasterArea.childTreeHeights === "fromGeotiff" &&
          this.props.rasterArea.childTreeHeightsFile === null
          ? tiffFiles[0]
          : this.props.rasterArea.childTreeHeights === "fromGeotiff"
            ? tiffFiles.find((x) => {
              return (
                x.name === this.props.rasterArea.childTreeHeightsFile ||
                x.name === this.props.rasterArea.childTreeHeightsFile.name ||
                x.path === this.props.rasterArea.childTreeHeightsFile ||
                x.path === this.props.rasterArea.childTreeHeightsFile?.path
              );
            })
            : this.props.rasterArea.childTreeHeightsFile;

      const request = {
        rasterParameters: {
          x_min: x_parent_min + Number(this.props.rasterArea.parentOffsetX),
          x_max: x_parent_max + Number(this.props.rasterArea.parentOffsetX),
          y_min: y_parent_min + Number(this.props.rasterArea.parentOffsetY),
          y_max: y_parent_max + Number(this.props.rasterArea.parentOffsetY),
          season: this.props.rasterArea.season,
          soil_Type: soilTypeIndex,
          resolution: parentResolution,
          rasterMethod: rasterMethodIndex,
          rasterizationPriority: this.props.rasterArea.rasterizationPriority,
          fillValue: this.props.rasterArea.fillValue.rasterValue,
          surfaceBelowTree:
            this.props.rasterArea.fillValueBelowTreePatches.rasterValue,

          additionalData: {
            globalParameters: {
              soilParameterization: "GloballyUniform",
              waterTemperatureParameteriation:
                this.props.rasterArea.globalWaterTemperatureSource,
              waterTemperatureGlobal:
                this.props.rasterArea.globalWaterTemperature,
              verticalResolutionMode:
                this.props.rasterArea.verticalResolutionMode,
              verticalResolutionMeter:
                this.props.rasterArea.verticalResolutionMode === "Manual"
                  ? this.props.rasterArea.verticalResolutionMeter
                  : this.props.rasterArea.rasterGridWidth,
              parentOffsetX: this.props.rasterArea.parentOffsetX,
              parentOffsetY: this.props.rasterArea.parentOffsetY,
              normalizeTopography: this.props.rasterArea.normalizeTopography,
              buildingSettings: {
                bhParent: this.props.rasterArea.buildingHeights,
                bhParentFile: toRasterHeightFilePayload(bhParentFile),
                bhChild: this.props.rasterArea.childBuildingHeights,
                bhChildFile: toRasterHeightFilePayload(bhChildFile),
              },
              treeSettings: {
                thParent: this.props.rasterArea.treeHeights,
                thParentFile: toRasterHeightFilePayload(thParentFile),
                thChild: this.props.rasterArea.childTreeHeights,
                thChildFile: toRasterHeightFilePayload(thChildFile),
              },
              roughnessSettings: {
                useRoughnessGeotiff: this.props.rasterArea.useRoughnessGeotiff,
                roughnessGeotiffFile: toRasterHeightFilePayload(
                  roughnessGeotiffFile
                ),
              },
              domainBufferSettings: {
                gridPointsNorth: this.props.rasterArea.gridPointsNorth,
                gridPointsSouth: this.props.rasterArea.gridPointsSouth,
                gridPointsWest: this.props.rasterArea.gridPointsWest,
                gridPointsEast: this.props.rasterArea.gridPointsEast,
                bufferFillType: this.props.rasterArea.bufferFillType,
                bufferFillValue:
                  this.props.rasterArea.bufferFillValue.rasterValue,
                bufferMethod: this.props.rasterArea.bufferMethod,
                bufferTopographyInterpMethod:
                  this.props.rasterArea.bufferTopographyInterpMethod,
              },
            },
          },
        },
        childRasterParameters: {
          x_min: x_child_min,
          x_max: x_child_max,
          y_min: y_child_min,
          y_max: y_child_max,
          season: this.props.rasterArea.season,
          soil_Type: soilTypeIndex,
          resolution: childResolution,
          rasterMethod: rasterMethodIndex,
          rasterizationPriority: this.props.rasterArea.rasterizationPriority,
          fillValue: this.props.rasterArea.fillValue.rasterValue,
          surfaceBelowTree:
            this.props.rasterArea.fillValueBelowTreePatches.rasterValue,
          topographyUrl:
            this.props.rasterArea.topographyForChildDomain ===
              "SameAsParentDomain"
              ? null
              : this.props.rasterArea.topographyForChildDomain,
        },
        geojsonUrl: geojsonUrl,
        polygonDataUrl: polygonDataUrl,
        drawnGeojsonUrl: drawnGeojsonUrl,
      };

      if (soilType === "UserDefined") {
        request.rasterParameters.additionalData.globalParameters.soilParameters =
        {
          alpha_vg: this.props.rasterArea.soilAlphaCoefficient,
          l_vg: this.props.rasterArea.lSoilCoefficient,
          n_vg: this.props.rasterArea.nSoilCoefficient,
          gamma_w_sat: this.props.rasterArea.soilHydraulicConductivity,
          m_sat: this.props.rasterArea.soilMoistureSaturation,
          m_fc: this.props.rasterArea.soilMoistureCapacity,
          m_wilt: this.props.rasterArea.soilMoistureWilting,
          m_res: this.props.rasterArea.soilMoistureContent,
        };
      }

      if (this.props.currentCase.applicationField === 5) {
        request.rasterParameters.additionalData.windTurbineParameters = {
          mode: this.props.rasterArea.simulationMode,
          precursor_case: this.props.rasterArea.precursorCase?.value
            ? {
              value: this.props.rasterArea.precursorCase.value,
              label: this.props.rasterArea.precursorCase.label,
            }
            : null,
          amount_directions: this.props.rasterArea.amountDirections,
          single_inflow_wind_direction:
            this.props.rasterArea.amountDirections === "1"
              ? this.props.rasterArea.singleInflowWindDirection ?? 270.0
              : null,
          grid_points_inflow: this.props.rasterArea.additionalGridpointsInflow,
          interpolation_distance_inflow:
            this.props.rasterArea.gridpointsForInflowTopographyInterpolation,
          interpolation_mode_inflow:
            this.props.rasterArea.topographyInterpolationModeInflow,
          grid_points_southnorth:
            this.props.rasterArea.additionalGridpointsSouthNorthEach,
          interpolation_distance_outflow:
            this.props.rasterArea.gridpointsForOutflowTopographyInterpolation,
          interpolation_mode_southnorth:
            this.props.rasterArea.topographyInterpolationModeSouthNorth,
          grid_points_outflow:
            this.props.rasterArea.additionalGridpointsOutflow,
          interpolation_mode_outflow:
            this.props.rasterArea.topographyInterpolationModeOutflow,
          interpolation_start_after_last_turbine:
            this.props.rasterArea
              .gridpointsAfterWhichOutflowInterpolationStarts,
          add_hub_height_onto_topography:
            this.props.rasterArea.addHubHeightOntoTopography,
        };
      }

      if (this.props.currentCase.applicationField === 5) {
        request.rasterParameters.additionalData.windTurbineParameters = {
          mode: this.props.rasterArea.simulationMode,
          precursor_case: this.props.rasterArea.precursorCase?.value
            ? {
                value: this.props.rasterArea.precursorCase.value,
                label: this.props.rasterArea.precursorCase.label,
              }
            : null,
          amount_directions: this.props.rasterArea.amountDirections,
          grid_points_inflow: this.props.rasterArea.additionalGridpointsInflow,
          interpolation_distance_inflow:
            this.props.rasterArea.gridpointsForInflowTopographyInterpolation,
          interpolation_mode_inflow:
            this.props.rasterArea.topographyInterpolationModeInflow,
          grid_points_southnorth:
            this.props.rasterArea.additionalGridpointsSouthNorthEach,
          interpolation_distance_outflow:
            this.props.rasterArea.gridpointsForOutflowTopographyInterpolation,
          interpolation_mode_southnorth:
            this.props.rasterArea.topographyInterpolationModeSouthNorth,
          grid_points_outflow:
            this.props.rasterArea.additionalGridpointsOutflow,
          interpolation_mode_outflow:
            this.props.rasterArea.topographyInterpolationModeOutflow,
          interpolation_start_after_last_turbine:
            this.props.rasterArea
              .gridpointsAfterWhichOutflowInterpolationStarts,
        };
      }

      console.log(JSON.stringify(request, null, 2));
      await this.saveWindTurbineSettings();
      await requestStaticDriverAsync(this.props.match.params.caseId, request);
    }
  }

  getSetupRoute = () => {
    const { currentCase, match } = this.props;
    const projectId = match.params.projectId;
    const caseId = match.params.caseId;
    const applicationField = currentCase?.applicationField;

    if (applicationField === 1) {
      return `/projects/${projectId}/cases/${caseId}/thermalComfort`;
    } else if (applicationField === 2) {
      return `/projects/${projectId}/cases/${caseId}/windComfort`;
    } else if (applicationField === 3) {
      return `/projects/${projectId}/cases/${caseId}/chemistry`;
    } else if (applicationField === 4) {
      return `/projects/${projectId}/cases/${caseId}/expert`;
    } else if (applicationField === 5) {
      return `/projects/${projectId}/cases/${caseId}/windTurbine`;
    } else {
      return `/projects/${projectId}/cases/${caseId}/setup`;
    }
  };

  getSetupIcon = () => {
    const applicationField = this.props.currentCase?.applicationField;
    if (applicationField === 1) {
      return Sun;
    } else if (applicationField === 2) {
      return Wind;
    } else if (applicationField === 3) {
      return Cloud;
    } else if (applicationField === 4) {
      return Clipboard;
    } else if (applicationField === 5) {
      return Zap;
    } else {
      return Box;
    }
  };

  goToSetup = () => {
    this.props.history.push(this.getSetupRoute());
  };

  render() {
    const { t } = this.props;
    let isStaticDriverProcessing = false;
    if (
      this.state.isStaticDriverProcessing ||
      (this.props.currentCase?.staticDriverJob &&
        (this.props.currentCase.staticDriverJob.status === 0 ||
          this.props.currentCase.staticDriverJob.status === 1 ||
          this.props.currentCase.staticDriverJob.status === "New" ||
          this.props.currentCase.staticDriverJob.status === "Processing"))
    ) {
      isStaticDriverProcessing = true;
    }

    const tiffFiles = (this.props.files || []).filter((x) => x.type === "image/tiff");
    const hasTiffFiles = tiffFiles.length > 0;

    return (
      <>
        {(this.props.simulationJobs?.length ||
          !!this.props.simulationNotifications) && (
            <p className="sim-warning">{t("RasterStaticLocked")}</p>
          )}

        <div className={`raster-settings-btn-wrapper`}>
          <button
            className={`raster-settings-btn ${this.props.rasterArea.isRasterAreaDraggable
              ? `raster-settings-btn-active`
              : ""
              }`}
            onClick={this.props.toggleRasterAreaDraggable}
            disabled={
              this.props.simulationJobs?.length ||
              !!this.props.simulationNotifications ||
              isStaticDriverProcessing ||
              this.props.isRasterProcessing
            }
          >
            <div className="btn-content-wrapper">
              <Move color={"#494949"} size={"16px"} strokeWidth={"1.8px"} />
              <div className="raster-btn-lbl">{t("MoveCentre")}</div>
            </div>
          </button>

          <button
            className="raster-settings-btn"
            onClick={this.props.resetRasterAreaProperties}
            disabled={
              this.props.simulationJobs?.length ||
              !!this.props.simulationNotifications ||
              isStaticDriverProcessing ||
              this.props.isRasterProcessing
            }
          >
            <div className="btn-content-wrapper">
              <RotateCcw
                color={"#494949"}
                size={"16px"}
                strokeWidth={"1.8px"}
              />

              <div className="raster-btn-lbl">{t("DefaultSettings")}</div>
            </div>
          </button>
        </div>

        <NumericInput
          name={"CentreEasting"}
          label={t("CentreEasting")}
          value={this.props.rasterArea.rasterCenterLngUtm}
          maxLength={"9"}
          onChange={(e) =>
            this.props.setRasterCenterLngUtm({
              rasterCenterLngUtm: e.target.value,
              crsDef: this.props.crsDef,
              hasRasterMoved: true,
            })
          }
          disabled={this.props.isRasterProcessing || isStaticDriverProcessing}
          unit={"m"}
          help={helpUrl}
        />

        <NumericInput
          name={"CentreNorthing"}
          label={t("CentreNorthing")}
          value={this.props.rasterArea.rasterCenterLatUtm}
          maxLength={"12"}
          onChange={(e) =>
            this.props.setRasterCenterLatUtm({
              rasterCenterLatUtm: e.target.value,
              crsDef: this.props.crsDef,
              hasRasterMoved: true,
            })
          }
          unit={"m"}
          disabled={this.props.isRasterProcessing || isStaticDriverProcessing}
          help={helpUrl}
        />
        <hr className="raster-settings-ruler" />
        <Select
          label={t("SelectDomainType")}
          value={this.props.rasterArea.domainType}
          options={domainType.map((x) => {
            return { label: t(`${x}`), value: x };
          })}
          onChange={(e) => this.props.setDomainType(e.target.value)}
          // disabled={
          //   this.props.currentCase.applicationField === 3 ||
          //   this.props.currentCase.applicationField === 5 ||
          //   isStaticDriverProcessing ||
          //   this.props.isRasterProcessing
          // }
          help={helpUrl}
        />

        {this.props.rasterArea.domainType === "SingleDomain" && (
          <>
            {this.props.currentCase.applicationField === 5 ? (
              <Select
                label={t("GridWidth")}
                value={this.props.rasterArea.rasterGridWidth.toString()}
                options={gridWidthOptions.map((x) => {
                  return { label: t(`${x}`), value: x };
                })}
                onChange={(e) =>
                  this.props.setRasterGridWidth({
                    rasterGridWidth: Number(e.target.value),
                    crsDef: this.props.crsDef,
                  })
                }
                disabled={
                  isStaticDriverProcessing || this.props.isRasterProcessing
                }
                help={helpUrl}
              />
            ) : (
              <NumericInput
                name={"Grid Width"}
                label={t("GridWidth")}
                value={
                  this.props.currentCase.applicationField === 2
                    ? 1
                    : this.props.rasterArea.rasterGridWidth
                }
                min={"1"}
                max={
                  this.props.currentCase.applicationField === 0 ||
                    this.props.currentCase.applicationField === 4
                    ? "50"
                    : "50"
                }
                step={"1"}
                unit={"m"}
                maxLength={"2"}
                onChange={(e) =>
                  this.props.setRasterGridWidth({
                    rasterGridWidth: e.target.value,
                    crsDef: this.props.crsDef,
                  })
                }
                disabled={
                  this.props.currentCase.applicationField === 2 ||
                  isStaticDriverProcessing ||
                  this.props.isRasterProcessing
                }
                onBlur={(e) => {
                  if (
                    this.props.rasterArea.rasterGridWidth <
                    Number(e.target.min) ||
                    this.props.rasterArea.rasterGridWidth > Number(e.target.max)
                  ) {
                    this.props.setRasterGridWidth({
                      rasterGridWidth: 5,
                      crsDef: this.props.crsDef,
                    });
                  }
                }}
                help={helpUrl}
              />
            )}
            <NumericInput
              name={"No.ofGridPointsInX-Direction"}
              label={t("NoOfGridPointsInXDirection")}
              value={this.props.rasterArea.rasterGridXNumber}
              maxLength={this.props.currentCase?.applicationField === 5 ? "4" : "3"}
              min={this.props.currentCase.applicationField === 2 ? 30 : 100}
              max={
                this.props.currentCase.applicationField === 2 ? 600
                  : this.props.currentCase?.applicationField === 5 ? 9999
                    : 1000
              }
              step={2}
              unit={"-"}
              onChange={(e) =>
                this.props.setRasterGridXNumber({
                  rasterGridXNumber: e.target.value,
                  crsDef: this.props.crsDef,
                })
              }
              onBlur={(e) => {
                if (
                  this.props.rasterArea.rasterGridXNumber <
                  Number(e.target.min) ||
                  this.props.rasterArea.rasterGridXNumber > Number(e.target.max)
                ) {
                  this.props.setRasterGridXNumber({
                    rasterGridXNumber: 200,
                    crsDef: this.props.crsDef,
                  });
                }
              }}
              disabled={
                this.props.isRasterProcessing || isStaticDriverProcessing
              }
              help={helpUrl}
            />
            <NumericInput
              name={"No.ofGridPointsInY-Direction"}
              label={t("NoOfGridPointsInYDirection")}
              value={this.props.rasterArea.rasterGridYNumber}
              maxLength={this.props.currentCase?.applicationField === 5 ? "4" : "3"}
              min={this.props.currentCase.applicationField === 2 ? 30 : 100}
              max={
                this.props.currentCase.applicationField === 2 ? 600
                  : this.props.currentCase?.applicationField === 5 ? 9999
                    : 1000
              }
              step={"2"}
              unit={"-"}
              onChange={(e) =>
                this.props.setRasterGridYNumber({
                  rasterGridYNumber: e.target.value,
                  crsDef: this.props.crsDef,
                })
              }
              onBlur={(e) => {
                if (
                  this.props.rasterArea.rasterGridYNumber <
                  Number(e.target.min) ||
                  this.props.rasterArea.rasterGridYNumber > Number(e.target.max)
                ) {
                  this.props.setRasterGridYNumber({
                    rasterGridYNumber: 200,
                    crsDef: this.props.crsDef,
                  });
                }
              }}
              disabled={
                this.props.isRasterProcessing || isStaticDriverProcessing
              }
              help={helpUrl}
            />
          </>
        )}

        {this.props.rasterArea.domainType === "NestedDomain" && (
          <>
            <Select
              label={t("SelectGridSizes")}
              value={this.props.rasterArea.gridSizes}
              options={
                this.props.currentCase.applicationField === 2
                  ? windGridSizes.map((x) => {
                    return { label: t(`${x}`), value: x };
                  })
                  : this.props.currentCase.applicationField === 0 ||
                    this.props.currentCase.applicationField === 4
                    ? staticExpertGridSizes.map((x) => {
                      return { label: t(`${x}`), value: x };
                    })
                    : gridSizes.map((x) => {
                      return { label: t(`${x}`), value: x };
                    })
              }
              onChange={(e) =>
                this.props.setGridSizes({
                  gridSizes: e.target.value,
                  crsDef: this.props.crsDef,
                })
              }
              disabled={
                this.props.isRasterProcessing || isStaticDriverProcessing
              }
              help={helpUrl}
            />
            <NumericInput
              name={"Parent: No. of GridPoints in x-Direction"}
              label={t("ParentNumberOfGridPointsInXDirection")}
              value={this.props.rasterArea.parentRasterGridXNumber}
              min={
                this.props.currentCase?.applicationField === 2 ? "30" : "100"
              }
              max={
                this.props.currentCase?.applicationField === 2
                  ? "600"
                  : this.props.currentCase?.applicationField === 1
                    ? "1000"
                    : this.props.currentCase?.applicationField === 0 ||
                      this.props.currentCase?.applicationField === 4
                      ? "1000"
                      : this.props.currentCase?.applicationField === 5
                        ? "9999"
                        : "400"
              }
              step={"2"}
              maxLength={this.props.currentCase?.applicationField === 5 ? "4" : "3"}
              unit={"-"}
              onChange={(e) =>
                this.props.setParentRasterGridXNumber({
                  parentRasterGridXNumber: e.target.value,
                  crsDef: this.props.crsDef,
                })
              }
              onBlur={(e) => {
                if (
                  this.props.rasterArea.parentRasterGridXNumber <
                  Number(e.target.min) ||
                  this.props.rasterArea.parentRasterGridXNumber >
                  Number(e.target.max)
                ) {
                  this.props.setParentRasterGridXNumber({
                    parentRasterGridXNumber: 200,
                    crsDef: this.props.crsDef,
                  });
                }
              }}
              disabled={
                this.props.isRasterProcessing || isStaticDriverProcessing
              }
              help={helpUrl}
            />
            <NumericInput
              name={"Parent: No.ofGridPoints in y-Direction"}
              label={t("ParentNumberOfGridPointsInYDirection")}
              value={this.props.rasterArea.parentRasterGridYNumber}
              min={
                this.props.currentCase?.applicationField === 2 ? "30" : "100"
              }
              max={
                this.props.currentCase?.applicationField === 2
                  ? "600"
                  : this.props.currentCase?.applicationField === 1
                    ? "1000"
                    : this.props.currentCase?.applicationField === 0 ||
                      this.props.currentCase?.applicationField === 4
                      ? "1000"
                      : this.props.currentCase?.applicationField === 5
                        ? "9999"
                        : "400"
              }
              step={"2"}
              maxLength={this.props.currentCase?.applicationField === 5 ? "4" : "3"}
              onChange={(e) =>
                this.props.setParentRasterGridYNumber({
                  parentRasterGridYNumber: e.target.value,
                  crsDef: this.props.crsDef,
                })
              }
              unit={"-"}
              onBlur={(e) => {
                if (
                  this.props.rasterArea.parentRasterGridYNumber <
                  Number(e.target.min) ||
                  this.props.rasterArea.parentRasterGridYNumber >
                  Number(e.target.max)
                ) {
                  this.props.setParentRasterGridYNumber({
                    parentRasterGridYNumber: "200",
                    crsDef: this.props.crsDef,
                  });
                }
              }}
              disabled={
                this.props.isRasterProcessing || isStaticDriverProcessing
              }
              help={helpUrl}
            />
            <NumericInput
              name={"Child: No. of GridPoints in x-Direction"}
              label={t("ChildNumberOfGridPointsInXDirection")}
              value={this.props.rasterArea.childRasterGridXNumber}
              min={
                this.props.currentCase?.applicationField === 2 ? "30" : "100"
              }
              max={
                this.props.currentCase?.applicationField === 2
                  ? "1000"
                  : this.props.currentCase?.applicationField === 1
                    ? "1000"
                    : this.props.currentCase?.applicationField === 0 ||
                      this.props.currentCase?.applicationField === 4
                      ? "1000"
                      : this.props.currentCase?.applicationField === 5
                        ? "2000"
                        : "400"
              }
              step={"2"}
              maxLength={"3"}
              onChange={(e) =>
                this.props.setChildRasterGridXNumber({
                  childRasterGridXNumber: e.target.value,
                  crsDef: this.props.crsDef,
                })
              }
              unit={"-"}
              onBlur={(e) => {
                if (
                  this.props.rasterArea.childRasterGridXNumber <
                  Number(e.target.min) ||
                  this.props.rasterArea.childRasterGridXNumber >
                  Number(e.target.max)
                ) {
                  this.props.setChildRasterGridXNumber({
                    childRasterGridXNumber: "200",
                    crsDef: this.props.crsDef,
                  });
                }
              }}
              disabled={
                this.props.isRasterProcessing || isStaticDriverProcessing
              }
              help={helpUrl}
            />
            <NumericInput
              name={"Child: No.ofGridPoints in y-Direction"}
              label={t("ChildNumberOfGridPointsInYDirection")}
              value={this.props.rasterArea.childRasterGridYNumber}
              min={
                this.props.currentCase?.applicationField === 2 ? "30" : "100"
              }
              max={
                this.props.currentCase?.applicationField === 2
                  ? "1000"
                  : this.props.currentCase?.applicationField === 1
                    ? "1000"
                    : this.props.currentCase?.applicationField === 0 ||
                      this.props.currentCase?.applicationField === 4
                      ? "1000"
                      : this.props.currentCase?.applicationField === 5
                        ? "2000"
                        : "400"
              }
              step={"2"}
              maxLength={"3"}
              unit={"-"}
              onChange={(e) =>
                this.props.setChildRasterGridYNumber({
                  childRasterGridYNumber: e.target.value,
                  crsDef: this.props.crsDef,
                })
              }
              onBlur={(e) => {
                if (
                  this.props.rasterArea.childRasterGridYNumber <
                  Number(e.target.min) ||
                  this.props.rasterArea.childRasterGridYNumber >
                  Number(e.target.max)
                ) {
                  this.props.setChildRasterGridYNumber({
                    childRasterGridYNumber: "200",
                    crsDef: this.props.crsDef,
                  });
                }
              }}
              disabled={
                this.props.isRasterProcessing || isStaticDriverProcessing
              }
              help={helpUrl}
            />
            <NumericInput
              name={"ParentOffsetXDirection"}
              label={t("ParentOffsetXDirection")}
              value={this.props.rasterArea.parentOffsetX}
              min={"-4999"}
              max={"4999"}
              step={"1"}
              maxLength={"4"}
              onChange={(e) =>
                this.props.setParentOffsetX({
                  parentOffsetX: e.target.value,
                  crsDef: this.props.crsDef,
                })
              }
              unit={"-"}
              onBlur={(e) => {
                if (
                  this.props.rasterArea.parentOffsetX > Number(e.target.max) ||
                  this.props.rasterArea.parentOffsetX < Number(e.target.min) ||
                  this.props.rasterArea.parentOffsetX === ""
                ) {
                  this.props.setParentOffsetX({
                    parentOffsetX: 0,
                    crsDef: this.props.crsDef,
                  });
                }
              }}
              disabled={
                this.props.isRasterProcessing || isStaticDriverProcessing
              }
              help={helpUrl}
            />
            <NumericInput
              name={"ParentOffsetYDirection"}
              label={t("ParentOffsetYDirection")}
              value={this.props.rasterArea.parentOffsetY}
              min={"-4999"}
              max={"4999"}
              step={1}
              maxLength={4}
              unit={"-"}
              onChange={(e) => {
                this.props.setParentOffsetY({
                  parentOffsetY: e.target.value,
                  crsDef: this.props.crsDef,
                });
              }}
              onBlur={(e) => {
                if (
                  this.props.rasterArea.parentOffsetY < -4999 ||
                  this.props.rasterArea.parentOffsetY > 4999 ||
                  this.props.rasterArea.parentOffsetY === ""
                ) {
                  this.props.setParentOffsetY({
                    parentOffsetY: "0",
                    crsDef: this.props.crsDef,
                  });
                }
              }}
              disabled={
                this.props.isRasterProcessing || isStaticDriverProcessing
              }
              help={helpUrl}
            />
          </>
        )}
        <hr className="raster-settings-ruler" />

        {this.props.currentCase.applicationField === 5 && (
          <>
            <Select
              label={t("SimulationMode")}
              value={this.props.rasterArea.simulationMode}
              options={simulationModeOptions.map((x) => {
                const hasPrecursorCases =
                  this.props.rasterArea.precursorCaseOptions &&
                  this.props.rasterArea.precursorCaseOptions.length > 0;
                return {
                  label: t(`${x}`),
                  value: x,
                  disabled: x === "FullRun" && !hasPrecursorCases,
                };
              })}
              onChange={(e) => {
                this.props.setSimulationMode(e.target.value);
                if (
                  e.target.value === "FullRun" &&
                  this.props.rasterArea.precursorCaseOptions &&
                  this.props.rasterArea.precursorCaseOptions.length > 0
                ) {
                  this.props.setPrecursorCase(
                    this.props.rasterArea.precursorCaseOptions[0]
                  );
                }
              }}
              disabled={
                isStaticDriverProcessing || this.props.isRasterProcessing
              }
              help={helpUrl}
            />

            {this.props.rasterArea.simulationMode === "FullRun" && (
              <>
                <Select
                  label={t("PrecursorCase")}
                  value={
                    this.props.rasterArea?.precursorCase?.value ||
                    (this.props.rasterArea.precursorCaseOptions &&
                      this.props.rasterArea.precursorCaseOptions.length > 0
                      ? this.props.rasterArea.precursorCaseOptions[0].value
                      : "")
                  }
                  options={this.props.rasterArea.precursorCaseOptions}
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    const selectedOption =
                      this.props.rasterArea.precursorCaseOptions?.find(
                        (option) => option.value === selectedValue
                      );
                    if (selectedOption) {
                      this.props.setPrecursorCase(selectedOption);
                    }
                  }}
                  disabled={
                    isStaticDriverProcessing || this.props.isRasterProcessing
                  }
                  help={helpUrl}
                />

                <Select
                  label={t("AmountDirections")}
                  value={this.props.rasterArea.amountDirections}
                  options={amountDirectionsOptions.map((x) => {
                    return { label: t(`${x}`), value: x };
                  })}
                  onChange={(e) =>
                    this.props.setAmountDirections(e.target.value)
                  }
                  disabled={
                    isStaticDriverProcessing || this.props.isRasterProcessing
                  }
                  help={helpUrl}
                />
                {this.props.rasterArea.amountDirections === "1" && (
                  <>
                    <NumericInput
                      name={"SingleInflowWindDirection"}
                      label={t("SingleInflowWindDirection")}
                      value={this.props.rasterArea.singleInflowWindDirection ?? 270.0}
                      min={"0"}
                      max={"360"}
                      step={"0.5"}
                      maxLength={"5"}
                      unit={"°"}
                      onChange={(e) =>
                        this.props.setSingleInflowWindDirection(
                          Number(e.target.value)
                        )
                      }
                      onBlur={(e) => {
                        if (
                          this.props.rasterArea.singleInflowWindDirection < Number(e.target.min) ||
                          this.props.rasterArea.singleInflowWindDirection > Number(e.target.max)
                        ) {
                          this.props.setSingleInflowWindDirection(270.0);
                        }
                      }}
                      disabled={
                        isStaticDriverProcessing || this.props.isRasterProcessing
                      }
                      help={helpUrl}
                    />
                    {/* <NumericInput
                      name={"DomainRotationClockwise"}
                      label={t("DomainRotationClockwise")}
                      value={this.props.rasterArea.domainRotationClockwise || 0}
                      min={"0"}
                      max={"359.5"}
                      step={"0.5"}
                      maxLength={"5"}
                      unit={"°"}
                      onChange={(e) =>
                        this.props.setDomainRotationClockwise(
                          Number(e.target.value)
                        )
                      }
                      disabled={
                        isStaticDriverProcessing || this.props.isRasterProcessing
                      }
                      help={helpUrl}
                    /> */}
                  </>
                )}
              </>
            )}

            <NumericInput
              name={"AdditionalGridpointsInflow"}
              label={t("AdditionalGridpointsInflow")}
              value={this.props.rasterArea.additionalGridpointsInflow}
              min={"0"}
              max={"256"}
              maxLength={"3"}
              step={"1"}
              unit={"-"}
              onChange={(e) =>
                this.props.setAdditionalGridpointsInflow({
                  additionalGridpointsInflow: Number(e.target.value),
                  crsDef: this.props.crsDef,
                })
              }
              disabled={
                isStaticDriverProcessing || this.props.isRasterProcessing
              }
              help={helpUrl}
            />

            {this.props.rasterArea.simulationMode === "FullRun" && (
              <>
                <NumericInput
                  name={"GridpointsForInflowTopographyInterpolation"}
                  label={t("GridpointsForInflowTopographyInterpolation")}
                  value={
                    this.props.rasterArea
                      .gridpointsForInflowTopographyInterpolation
                  }
                  min={"0"}
                  max={"256"}
                  maxLength={"3"}
                  step={"1"}
                  unit={"-"}
                  onChange={(e) =>
                    this.props.setGridpointsForInflowTopographyInterpolation(
                      Number(e.target.value)
                    )
                  }
                  disabled={
                    isStaticDriverProcessing || this.props.isRasterProcessing
                  }
                  help={helpUrl}
                />

                <Select
                  label={t("TopographyInterpolationModeInflow")}
                  value={
                    this.props.rasterArea.topographyInterpolationModeInflow
                  }
                  options={topographyInterpolationModeOptions.map((x) => {
                    return { label: t(`${x}`), value: x };
                  })}
                  onChange={(e) =>
                    this.props.setTopographyInterpolationModeInflow(
                      e.target.value
                    )
                  }
                  disabled={
                    isStaticDriverProcessing || this.props.isRasterProcessing
                  }
                  help={helpUrl}
                />
              </>
            )}

            <NumericInput
              name={"AdditionalGridpointsSouthNorthEach"}
              label={t("AdditionalGridpointsSouthNorthEach")}
              value={this.props.rasterArea.additionalGridpointsSouthNorthEach}
              min={"0"}
              max={"256"}
              maxLength={"3"}
              step={"1"}
              unit={"-"}
              onChange={(e) =>
                this.props.setAdditionalGridpointsSouthNorthEach({
                  additionalGridpointsSouthNorthEach: Number(e.target.value),
                  crsDef: this.props.crsDef,
                })
              }
              disabled={
                isStaticDriverProcessing || this.props.isRasterProcessing
              }
              help={helpUrl}
            />

            {this.props.rasterArea.simulationMode === "FullRun" && (
              <>
                <NumericInput
                  name={"GridpointsForOutflowTopographyInterpolation"}
                  label={t("GridpointsForOutflowTopographyInterpolation")}
                  value={
                    this.props.rasterArea
                      .gridpointsForOutflowTopographyInterpolation
                  }
                  min={"0"}
                  max={"256"}
                  maxLength={"3"}
                  step={"1"}
                  unit={"-"}
                  onChange={(e) =>
                    this.props.setGridpointsForOutflowTopographyInterpolation(
                      Number(e.target.value)
                    )
                  }
                  disabled={
                    isStaticDriverProcessing || this.props.isRasterProcessing
                  }
                  help={helpUrl}
                />

                <Select
                  label={t("TopographyInterpolationModeSouthNorth")}
                  value={
                    this.props.rasterArea.topographyInterpolationModeSouthNorth
                  }
                  options={topographyInterpolationModeOptions.map((x) => {
                    return { label: t(`${x}`), value: x };
                  })}
                  onChange={(e) =>
                    this.props.setTopographyInterpolationModeSouthNorth(
                      e.target.value
                    )
                  }
                  disabled={
                    isStaticDriverProcessing || this.props.isRasterProcessing
                  }
                  help={helpUrl}
                />
              </>
            )}

            <NumericInput
              name={"AdditionalGridpointsOutflow"}
              label={t("AdditionalGridpointsOutflow")}
              value={this.props.rasterArea.additionalGridpointsOutflow}
              min={"0"}
              max={"512"}
              maxLength={"3"}
              step={"1"}
              unit={"-"}
              onChange={(e) =>
                this.props.setAdditionalGridpointsOutflow({
                  additionalGridpointsOutflow: Number(e.target.value),
                  crsDef: this.props.crsDef,
                })
              }
              disabled={
                isStaticDriverProcessing || this.props.isRasterProcessing
              }
              help={helpUrl}
            />

            {this.props.rasterArea.simulationMode === "FullRun" && (
              <>
                <Select
                  label={t("TopographyInterpolationModeOutflow")}
                  value={
                    this.props.rasterArea.topographyInterpolationModeOutflow
                  }
                  options={topographyInterpolationModeOutflowOptions.map(
                    (x) => {
                      return { label: t(`${x}`), value: x };
                    }
                  )}
                  onChange={(e) =>
                    this.props.setTopographyInterpolationModeOutflow(
                      e.target.value
                    )
                  }
                  disabled={
                    isStaticDriverProcessing || this.props.isRasterProcessing
                  }
                  help={helpUrl}
                />

                <NumericInput
                  name={"GridpointsAfterWhichOutflowInterpolationStarts"}
                  label={t("GridpointsAfterWhichOutflowInterpolationStarts")}
                  value={
                    this.props.rasterArea
                      .gridpointsAfterWhichOutflowInterpolationStarts
                  }
                  min={"0"}
                  max={"256"}
                  maxLength={"3"}
                  step={"1"}
                  unit={"-"}
                  onChange={(e) =>
                    this.props.setGridpointsAfterWhichOutflowInterpolationStarts(
                      Number(e.target.value)
                    )
                  }
                  disabled={
                    isStaticDriverProcessing || this.props.isRasterProcessing
                  }
                  help={helpUrl}
                />
                <Select
                  label={t("UseRoughnessGeotiff")}
                  value={this.props.rasterArea.useRoughnessGeotiff ? "Yes" : "No"}
                  options={
                    hasTiffFiles
                      ? [
                        { label: t("No"), value: "No" },
                        { label: t("Yes"), value: "Yes" },
                      ]
                      : [{ label: t("No"), value: "No" }]
                  }
                  onChange={(e) =>
                    this.props.setUseRoughnessGeotiff(e.target.value === "Yes")
                  }
                  disabled={
                    isStaticDriverProcessing || this.props.isRasterProcessing
                  }
                  help={helpUrl}
                />

                {this.props.rasterArea.useRoughnessGeotiff && (
                  <Select
                    label={t("RoughnessLengthGeotiff")}
                    value={this.props.rasterArea.roughnessGeotiffFile}
                    options={tiffFiles.map((x) => {
                      return { label: x.name, value: x.path };
                    })}
                    onChange={(e) =>
                      this.props.setRoughnessGeotiffFile(e.target.value)
                    }
                    disabled={
                      isStaticDriverProcessing ||
                      this.props.isRasterProcessing ||
                      !hasTiffFiles
                    }
                    help={helpUrl}
                  />
                )}
              </>
            )}

            <div className="form-group">
              <label
                className="form-label"
                htmlFor="add-hub-height-onto-topography"
              >
                {t("AddHubHeightOntoTopography")}
              </label>
              <div style={{ border: 0, width: "94%" }}>
                <input
                  id="add-hub-height-onto-topography"
                  type="checkbox"
                  aria-label={t("AddHubHeightOntoTopography")}
                  className="form-input form-checkbox-input"
                  checked={this.props.rasterArea.addHubHeightOntoTopography}
                  onChange={(e) =>
                    this.props.setAddHubHeightOntoTopography(
                      e.target.checked
                    )
                  }
                  disabled={
                    isStaticDriverProcessing || this.props.isRasterProcessing
                  }
                />
              </div>
              <a
                className="help-icon-link"
                href={helpUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <HelpCircle
                  color="rgba(0, 0, 0, 0.75)"
                  strokeWidth="2"
                  size="26px"
                  style={{
                    alignSelf: "center",
                    paddingLeft: "4px",
                  }}
                />
              </a>
            </div>
          </>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gridTemplateRows: "auto auto",
            gap: "12px",
            marginTop: "24px",
          }}
        >
          <button
            className="raster-settings-btn"
            onClick={this.createRasterPreview}
            disabled={
              this.props.isRasterProcessing ||
              this.state.isStaticDriverProcessing ||
              this.props.currentCase.rasterPreviewJob?.status === 0 ||
              this.props.currentCase.rasterPreviewJob?.status === 1 ||
              this.props.currentCase.rasterPreviewJob?.status === "New" ||
              this.props.currentCase.rasterPreviewJob?.status ===
              "Processing" ||
              this.props.currentCase.staticDriverJob?.status === 0 ||
              this.props.currentCase.staticDriverJob?.status === 1 ||
              this.props.currentCase.staticDriverJob?.status === 2 ||
              this.props.currentCase.staticDriverJob?.status === "New" ||
              this.props.currentCase.staticDriverJob?.status === "Processing" ||
              this.props.currentCase.staticDriverJob?.status === "Completed"
            }
            style={{
              padding:
                this.props.language && this.props.language === "de-DE"
                  ? "6px 5px"
                  : "6px 10px",
            }}
          >
            <div className="btn-content-wrapper">
              {this.props.isRasterProcessing ||
                this.props.currentCase.rasterPreviewJob?.status === 0 ||
                this.props.currentCase.rasterPreviewJob?.status === 1 ||
                this.props.currentCase.rasterPreviewJob?.status === "New" ||
                this.props.currentCase.rasterPreviewJob?.status ===
                "Processing" ? (
                <Loader size="xx-small" />
              ) : (
                <Grid color={"#494949"} size={"16px"} strokeWidth={"1.8px"} />
              )}
              <div className="raster-btn-lbl">{t("CreateRasterPreview")}</div>
            </div>
          </button>

          <button
            className="raster-settings-btn"
            onClick={this.createStaticDriver}
            disabled={
              (this.props.currentCase.rasterPreviewJob?.status !== 2 &&
                this.props.currentCase.rasterPreviewJob?.status !==
                "Completed") ||
              this.props.isRasterProcessing ||
              this.state.isStaticDriverProcessing ||
              this.props.currentCase.staticDriverJob?.status === 0 ||
              this.props.currentCase.staticDriverJob?.status === 1 ||
              this.props.currentCase.staticDriverJob?.status === "New" ||
              this.props.currentCase.staticDriverJob?.status === "Processing" ||
              this.props.currentCase.staticDriverJob?.status === 2 ||
              this.props.currentCase.staticDriverJob?.status === "Completed" ||
              this.props.simulationJobs?.length ||
              !!this.props.simulationNotifications ||
              this.props.rasterArea.hasGeometryChanged
            }
            style={{
              padding:
                this.props.language && this.props.language === "de-DE"
                  ? "6px 5px"
                  : "6px 10px",
            }}
          >
            <div className={`btn-content-wrapper `}>
              {this.state.isStaticDriverProcessing ||
                this.props.currentCase.staticDriverJob?.status === 0 ||
                this.props.currentCase.staticDriverJob?.status === 1 ||
                this.props.currentCase.staticDriverJob?.status === "New" ||
                this.props.currentCase.staticDriverJob?.status ===
                "Processing" ? (
                <Loader size="xx-small" />
              ) : (
                <Airplay color={"#494949"} size={"16px"} strokeWidth={"2px"} />
              )}
              <div className="raster-btn-lbl">{t("CreateStaticDriver")}</div>
            </div>
          </button>

          <button
            className="raster-settings-btn"
            onClick={this.goToSetup}
            disabled={
              !this.props.currentCase.staticDriverJob ||
              (this.props.currentCase.staticDriverJob.status !== 2 &&
                this.props.currentCase.staticDriverJob.status !== "Completed")
            }
            style={{
              marginTop: "0px",
              borderRadius: "3px",
              padding:
                this.props.language && this.props.language === "de-DE"
                  ? "6px 5px"
                  : "6px 10px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "5px",
              }}
            >
              <Icon
                className="project-btn"
                icon={this.getSetupIcon()}
                color={"#494949"}
                strokeWidth={"2px"}
              />
              <span>{t("Setup")}</span>
            </div>
          </button>

          {(this.props.currentCase.staticDriverJob?.status === 2 ||
            this.props.currentCase.staticDriverJob?.status ===
            "Completed") && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CheckCircle size="16px" color="rgb(6, 137, 108)" />
                <p
                  style={{
                    margin: "0px",
                    marginLeft: "9px",
                    cursor: "default",
                  }}
                  className={`new-layer-name-error new-layer-success `}
                >
                  {t("StaticDriverHasBeenCreated")}
                </p>
              </div>
            )}

          {(this.props.currentCase?.staticDriverJob?.status === 3 ||
            this.props.currentCase?.staticDriverJob?.status === "Error") && (
              <div
                style={{
                  display: "flex",
                  height: "fit-content",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <div>
                  <XCircle
                    color={"#a81d1dcf"}
                    size={"14px"}
                    strokeWidth={"1.8px"}
                  />
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#a81d1dcf",
                    paddingLeft: "6px",
                  }}
                >
                  {t(
                    errorCodes[this.props.currentCase.staticDriverJob.error]
                  )}
                </div>
              </div>
            )}
        </div>
        {(this.props.currentCase?.rasterPreviewJob?.status === 3 ||
          this.props.currentCase?.rasterPreviewJob?.status === "Error") && (
            <div
              style={{
                display: "flex",
                height: "fit-content",
                alignItems: "center",
                marginTop: "24px",
              }}
            >
              <div>
                <XCircle
                  color={"#a81d1dcf"}
                  size={"14px"}
                  strokeWidth={"1.8px"}
                />
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#a81d1dcf",
                  paddingLeft: "6px",
                }}
              >
                {t(errorCodes[this.props.currentCase.rasterPreviewJob.error])}
              </div>
            </div>
          )}
        {this.props.rasterArea.rasterPreview !== null &&
          this.props.rasterArea.hasGeometryChanged &&
          this.props.currentCase.rasterPreviewJob?.status !== 0 &&
          this.props.currentCase.rasterPreviewJob?.status !== 1 &&
          this.props.currentCase.rasterPreviewJob?.status !== "New" &&
          this.props.currentCase.rasterPreviewJob?.status !== "Processing" &&
          this.props.currentCase.staticDriverJob?.status !== 2 &&
          this.props.currentCase.staticDriverJob?.status !== "Completed" && (
            <div
              style={{
                display: "flex",
                height: "fit-content",
                alignItems: "center",
                marginTop: "12px",
              }}
            >
              <div>
                <XCircle
                  color={"#a81d1dcf"}
                  size={"14px"}
                  strokeWidth={"1.8px"}
                />
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#a81d1dcf",
                  paddingLeft: "6px",
                }}
              >
                {t("RasterGeometryChangedWarning")}
              </div>
            </div>
          )}
        {this.props.rasterArea.rasterPreview !== null &&
          this.props.rasterArea.parentRasterPreview === null && (
            <>
              <div style={{ display: "flex", flexDirection: "row" }}>
                <div style={{ width: "50%", height: "fit-content" }}>
                  <label className="legend-row-container legend-wrapper">
                    <input
                      type="checkbox"
                      className="raster-checkbox"
                      checked={this.props.rasterArea.isRasterPreviewVisible}
                      onChange={() => this.props.toggleRasterPreviewVisibility()}
                      disabled={
                        this.props.isRasterProcessing || isStaticDriverProcessing
                      }
                    />

                    <label className="raster-object-name">
                      {t("RasterPreview")}
                    </label>
                  </label>

                  <label className="legend-row-container legend-wrapper">
                    <input
                      type="checkbox"
                      className="raster-checkbox"
                      checked={this.props.rasterArea.isGeoJsonVisible}
                      onChange={() => this.props.toggleGeoJsonVisibility()}
                      disabled={
                        this.props.isRasterProcessing || isStaticDriverProcessing
                      }
                    />

                    <label className="raster-object-name">
                      {t("GeojsonPreview")}
                    </label>
                  </label>

                  <label className="legend-row-container legend-wrapper">
                    <input
                      type="checkbox"
                      className="raster-checkbox"
                      checked={this.props.rasterArea.isRasterAreaVisible}
                      onChange={() => this.props.toggleRasterAreaVisibility()}
                      disabled={
                        this.props.isRasterProcessing || isStaticDriverProcessing
                      }
                    />

                    <label className="raster-object-name">
                      {t("RasterAreaPreview")}
                    </label>
                  </label>
                  <label className="legend-row-container legend-wrapper">
                    <input
                      type="checkbox"
                      className="raster-checkbox"
                      checked={this.props.rasterArea.isBufferAreaVisible}
                      onChange={() => this.props.toggleBufferAreaVisibility()}
                      disabled={
                        this.props.isRasterProcessing || isStaticDriverProcessing
                      }
                    />

                    <label className="raster-object-name">
                      {t("BufferAreaPreview")}
                    </label>
                  </label>
                  {this.props.currentCase?.applicationField === 5 && (
                    <label className="legend-row-container legend-wrapper">
                      <input
                        type="checkbox"
                        className="raster-checkbox"
                        checked={
                          this.props.rasterArea.isWindTurbineFlowAreaVisible
                        }
                        onChange={() =>
                          this.props.toggleWindTurbineFlowAreaVisibility()
                        }
                        disabled={
                          this.props.isRasterProcessing || isStaticDriverProcessing
                        }
                      />
                      <label className="raster-object-name">
                        {t("WindTurbineFlowArea")}
                      </label>
                    </label>
                  )}
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    width: "50%",
                  }}
                >
                </div>
              </div>
            </>
          )}
        {this.props.rasterArea.parentRasterPreview !== null && (
          <div style={{ display: "flex", flexDirection: "row" }}>
            <div style={{ width: "50%", height: "fit-content" }}>
              <label className="legend-row-container legend-wrapper">
                <input
                  type="checkbox"
                  className="raster-checkbox"
                  checked={this.props.rasterArea.isParentRasterPreviewVisible}
                  onChange={() =>
                    this.props.toggleParentRasterPreviewVisibility()
                  }
                  disabled={
                    this.props.isRasterProcessing || isStaticDriverProcessing
                  }
                />
                <label className="raster-object-name">
                  {t("ParentRasterPreview")}
                </label>
              </label>
              <label className="legend-row-container legend-wrapper">
                <input
                  type="checkbox"
                  className="raster-checkbox"
                  checked={this.props.rasterArea.isChildRasterPreviewVisible}
                  onChange={() =>
                    this.props.toggleChildRasterPreviewVisibility()
                  }
                  disabled={
                    this.props.isRasterProcessing || isStaticDriverProcessing
                  }
                />
                <label className="raster-object-name">
                  {t("ChildRasterPreview")}
                </label>
              </label>
              <label className="legend-row-container legend-wrapper">
                <input
                  type="checkbox"
                  className="raster-checkbox"
                  checked={this.props.rasterArea.isGeoJsonVisible}
                  onChange={() => this.props.toggleGeoJsonVisibility()}
                  disabled={
                    this.props.isRasterProcessing || isStaticDriverProcessing
                  }
                />
                <label className="raster-object-name">
                  {t("GeojsonPreview")}
                </label>
              </label>
              <label className="legend-row-container legend-wrapper">
                <input
                  type="checkbox"
                  className="raster-checkbox"
                  checked={this.props.rasterArea.isParentRasterAreaVisible}
                  onChange={() => this.props.toggleParentRasterAreaVisibility()}
                  disabled={
                    this.props.isRasterProcessing || isStaticDriverProcessing
                  }
                />
                <label className="raster-object-name">
                  {t("ParentRasterArea")}
                </label>
              </label>
              <label className="legend-row-container legend-wrapper">
                <input
                  type="checkbox"
                  className="raster-checkbox"
                  checked={this.props.rasterArea.isChildRasterAreaVisible}
                  onChange={() => this.props.toggleChildRasterAreaVisibility()}
                  disabled={
                    this.props.isRasterProcessing || isStaticDriverProcessing
                  }
                />
                <label className="raster-object-name">
                  {t("ChildRasterArea")}
                </label>
              </label>
              <label className="legend-row-container legend-wrapper">
                <input
                  type="checkbox"
                  className="raster-checkbox"
                  checked={this.props.rasterArea.isBufferAreaVisible}
                  onChange={() => this.props.toggleBufferAreaVisibility()}
                  disabled={
                    this.props.isRasterProcessing || isStaticDriverProcessing
                  }
                />

                <label className="raster-object-name">
                  {t("BufferAreaPreview")}
                </label>
              </label>
              {this.props.currentCase?.applicationField === 5 && (
                <label className="legend-row-container legend-wrapper">
                  <input
                    type="checkbox"
                    className="raster-checkbox"
                    checked={
                      this.props.rasterArea.isWindTurbineFlowAreaVisible
                    }
                    onChange={() =>
                      this.props.toggleWindTurbineFlowAreaVisibility()
                    }
                    disabled={
                      this.props.isRasterProcessing || isStaticDriverProcessing
                    }
                  />
                  <label className="raster-object-name">
                    {t("WindTurbineFlowArea")}
                  </label>
                </label>
              )}
            </div>
          </div>
        )}
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    geoJsonWg: state.map.geoJsonWg,
    polygonData: state.map.polygonData,
    rasterArea: state.rasterArea,
    loadedProject: state.projects.loadedProject,
    notifications: state.notifications,
    drawnPolygonGeojsonWg: state.map.drawnPolygonGeojsonWg,
    treeGeoJsonUtm: state.map.treeGeoJsonUtm,
    allTreesGeoProps: state.map.allTreesGeoProps,
    allDrawnTreesCoordinatesUtm: state.map.allDrawnTreesCoordinatesUtm,
    drawnGeoJson: state.map.drawnGeoJson,
    currentCase: state.projects.currentCase,
    crsDef: state.map.crsDef,
    simulationNotifications: selectCaseSimulationNotifications(state),
    simulationJobs: state.projects.currentCase?.simulationJobs,
    emissionGeoJsonWg: state.map.emissionGeoJsonWg,
    mapPoiGeoJsonWg: state.map.mapPoiGeoJsonWg,
    windTurbineGeoJsonWg: state.map.windTurbineGeoJsonWg,
    emissionDrawnGeoJsonUtm: state.map.emissionDrawnGeoJsonUtm,
    mapPoiDrawnGeoJsonUtm: state.map.mapPoiDrawnGeoJsonUtm,
    windTurbineDrawnGeoJsonUtm: state.map.windTurbineDrawnGeoJsonUtm,
    language: state.userSettings?.settings?.preferredLanguage,
    files: state.projects?.loadedProject?.files,
    caseDetailsDto: state.projects?.loadedProject?.caseDetailsDto,
    windTurbineSettings: state.windTurbine,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setRasterCenterLatUtm: (lat) =>
      dispatch(actions.setRasterCenterLatUtm(lat)),
    setRasterCenterLngUtm: (lng) =>
      dispatch(actions.setRasterCenterLngUtm(lng)),
    setRasterGridWidth: (gridWidth) =>
      dispatch(actions.setRasterGridWidth(gridWidth)),
    setRasterGridXNumber: (gridXNumber) =>
      dispatch(actions.setRasterGridXNumber(gridXNumber)),
    setRasterGridYNumber: (gridYNumber) =>
      dispatch(actions.setRasterGridYNumber(gridYNumber)),
    toggleRasterAreaDraggable: () =>
      dispatch(actions.toggleRasterAreaDraggable()),
    resetRasterAreaProperties: () =>
      dispatch(actions.resetRasterAreaProperties()),
    toggleRasterPreviewVisibility: () =>
      dispatch(actions.toggleRasterPreviewVisibility()),
    toggleParentRasterPreviewVisibility: () =>
      dispatch(actions.toggleParentRasterPreviewVisibility()),
    toggleChildRasterPreviewVisibility: () =>
      dispatch(actions.toggleChildRasterPreviewVisibility()),
    toggleGeoJsonVisibility: () => dispatch(actions.toggleGeoJsonVisibility()),
    toggleRasterAreaVisibility: () =>
      dispatch(actions.toggleRasterAreaVisibility()),
    toggleParentRasterAreaVisibility: () =>
      dispatch(actions.toggleParentRasterAreaVisibility()),
    toggleChildRasterAreaVisibility: () =>
      dispatch(actions.toggleChildRasterAreaVisibility()),
    getRasterPreview: (raster) => dispatch(actions.getRasterPreview(raster)),
    getNestedRasterPreview: (raster) =>
      dispatch(actions.getNestedRasterPreview(raster)),
    onSingleRasterLoadVisibility: () =>
      dispatch(actions.onSingleRasterLoadVisibility()),
    onNestedRasterLoadVisibility: () =>
      dispatch(actions.onNestedRasterLoadVisibility()),
    setDomainType: (domainType) => dispatch(actions.setDomainType(domainType)),
    setGridSizes: (gridSizes) => dispatch(actions.setGridSizes(gridSizes)),
    setParentRasterGridXNumber: (gridXNumber) =>
      dispatch(actions.setParentRasterGridXNumber(gridXNumber)),
    setParentRasterGridYNumber: (gridYNumber) =>
      dispatch(actions.setParentRasterGridYNumber(gridYNumber)),
    setChildRasterGridXNumber: (gridXNumber) =>
      dispatch(actions.setChildRasterGridXNumber(gridXNumber)),
    setChildRasterGridYNumber: (gridYNumber) =>
      dispatch(actions.setChildRasterGridYNumber(gridYNumber)),
    createSingleTreeGeoJson: () => dispatch(createSingleTreeGeoJson()),
    setCaseDrawnGeojsonUrl: (payload) =>
      dispatch(setCaseDrawnGeojsonUrl(payload)),
    setSingleRasterCaseProps: (payload) =>
      dispatch(actions.setSingleRasterCaseProps(payload)),
    setNestedRasterCaseProps: (payload) =>
      dispatch(actions.setNestedRasterCaseProps(payload)),
    setDrawnPolygonGeojsonCrs: (payload) =>
      dispatch(setDrawnPolygonGeojsonCrs(payload)),
    createSingleEmissionDrawnGeoJson: (payload) =>
      dispatch(createSingleEmissionDrawnGeoJson(payload)),
    createSingleMapPoiDrawnGeoJson: (payload) =>
      dispatch(createSingleMapPoiDrawnGeoJson(payload)),
    createSingleWindTurbineDrawnGeoJson: (payload) =>
      dispatch(createSingleWindTurbineDrawnGeoJson(payload)),
    setParentOffsetX: (payload) => dispatch(actions.setParentOffsetX(payload)),
    setParentOffsetY: (payload) => dispatch(actions.setParentOffsetY(payload)),
    toggleBufferAreaVisibility: (payload) =>
      dispatch(actions.toggleBufferAreaVisibility(payload)),
    toggleWindTurbineFlowAreaVisibility: () =>
      dispatch(actions.toggleWindTurbineFlowAreaVisibility()),
    setSimulationMode: (payload) =>
      dispatch(actions.setSimulationMode(payload)),
    setAmountDirections: (payload) =>
      dispatch(actions.setAmountDirections(payload)),
    // setDomainRotationClockwise: (payload) =>
    //   dispatch(actions.setDomainRotationClockwise(payload)),
    setSingleInflowWindDirection: (payload) =>
      dispatch(actions.setSingleInflowWindDirection(payload)),
    setAdditionalGridpointsInflow: (payload) =>
      dispatch(actions.setAdditionalGridpointsInflow(payload)),
    setGridpointsForInflowTopographyInterpolation: (payload) =>
      dispatch(actions.setGridpointsForInflowTopographyInterpolation(payload)),
    setTopographyInterpolationModeInflow: (payload) =>
      dispatch(actions.setTopographyInterpolationModeInflow(payload)),
    setAdditionalGridpointsSouthNorthEach: (payload) =>
      dispatch(actions.setAdditionalGridpointsSouthNorthEach(payload)),
    setGridpointsForOutflowTopographyInterpolation: (payload) =>
      dispatch(actions.setGridpointsForOutflowTopographyInterpolation(payload)),
    setTopographyInterpolationModeSouthNorth: (payload) =>
      dispatch(actions.setTopographyInterpolationModeSouthNorth(payload)),
    setAdditionalGridpointsOutflow: (payload) =>
      dispatch(actions.setAdditionalGridpointsOutflow(payload)),
    setTopographyInterpolationModeOutflow: (payload) =>
      dispatch(actions.setTopographyInterpolationModeOutflow(payload)),
    setGridpointsAfterWhichOutflowInterpolationStarts: (payload) =>
      dispatch(
        actions.setGridpointsAfterWhichOutflowInterpolationStarts(payload)
      ),
    setUseRoughnessGeotiff: (payload) =>
      dispatch(actions.setUseRoughnessGeotiff(payload)),
    setRoughnessGeotiffFile: (payload) =>
      dispatch(actions.setRoughnessGeotiffFile(payload)),
    setAddHubHeightOntoTopography: (payload) =>
      dispatch(actions.setAddHubHeightOntoTopography(payload)),
    setPrecursorCase: (payload) => dispatch(actions.setPrecursorCase(payload)),
    setPrecursorCaseOptions: (payload) =>
      dispatch(actions.setPrecursorCaseOptions(payload)),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(withTranslation()(BasicRasterizationSettings)));
